// © 2001-2015 INOVx Solutions, Inc.
// 114 Pacifica Suite 300, Irvine, CA 92618 USA
// All rights reserved.
//
// This software is confidential, proprietary and unpublished property of
// INOVx Solutions, Inc.  It may NOT be copied in part or in whole on any
// medium, either electronic or printed, without the express written consent
// of INOVx Solutions, Inc.
import * as BABYLON from 'babylonjs';
import * as Data from '../../viewers/Data/WebData';

import * as WorkerGeometry from './WorkerGeometry';
class MeshFactory {

    public static Epsilon: number = .0000000001;
    private _scene: BABYLON.Scene;

    // Complexity Defined Meshes
    private _boxMasterMesh: BABYLON.Mesh;
    private _cylinderMasterMesh: BABYLON.Mesh;
    private _cylinderMasterMeshes: { [complexity: string]: BABYLON.Mesh; } = {};
    private _cylinderMasterMeshKeys: number[] = [];
    private _tripodMeshes: { [n: string]: BABYLON.Mesh } = {};
    private _boxMasterGeometry: WorkerGeometry.GeometryData;
    private _cylinderMasterGeometries: { [complexity: string]: WorkerGeometry.GeometryData; } = {};
    private _sphereMasterGeometries: { [complexity: string]: WorkerGeometry.GeometryData; } = {};

    private static DISTANCE_TOLERANCE = 1e-5;
    private static ZERO_REVOLVE_RADIUS = 0.1;

    private static XAXIS = new BABYLON.Vector3(1.0, 0.0, 0.0);
    private static YAXIS = new BABYLON.Vector3(0.0, 1.0, 0.0);
    private static ZAXIS = new BABYLON.Vector3(0.0, 0.0, 1.0);

    constructor(scene: BABYLON.Scene) {

        this._scene = scene;

        this._boxMasterMesh = BABYLON.Mesh.CreateBox("masterBox", 1, this._scene);
        this._boxMasterMesh.setEnabled(false);
        this._boxMasterGeometry = WorkerGeometry.Utils.fromMesh(this._boxMasterMesh);

        this._cylinderMasterMesh = BABYLON.Mesh.CreateCylinder("Cylinder", 1, 1, 1, 12, 1, this._scene);
        this._cylinderMasterMesh.setEnabled(false);
        this._cylinderMasterGeometries[12] = WorkerGeometry.Utils.fromMesh(this._cylinderMasterMesh);
    }

    dispose() {
        this._boxMasterMesh.dispose();

        this._cylinderMasterMesh.dispose();

        for (var key in this._cylinderMasterMeshes) {
            this._cylinderMasterMeshes[key].dispose();
            delete this._cylinderMasterMeshes[key];
        }
        this._cylinderMasterMesh = null;
        this._boxMasterMesh = null;
        this._boxMasterGeometry = null;
        this._tripodMeshes = {};
        this._cylinderMasterMeshes = {};
        this._cylinderMasterGeometries = {};
        this._sphereMasterGeometries = {};
        this._cylinderMasterMeshKeys = [];
    }

    private measureDirectGeometry<T>(factory: () => T): T {
        return factory();
    }

    private measureMeshBackedGeometry<T>(factory: () => T): T {
        return factory();
    }

    createBoxGeometry(id: string, materialId: string, boxInfo: Data.GraphicData.Box): WorkerGeometry.GeometryData {
        return this.measureDirectGeometry(() => {
            boxInfo.validate();

            var scale = new BABYLON.Vector3(boxInfo.Scale.Y / 1000, boxInfo.Scale.Z / 1000, boxInfo.Scale.X / 1000);
            var matrix = MeshFactory.composeTransformMatrix(scale, boxInfo.Position, boxInfo.Rotation);

            return WorkerGeometry.Utils.transform(this._boxMasterGeometry, matrix, id, id);
        });
    }

    createDishGeometry(id: string, materialId: string, dishInfo: Data.GraphicData.Dish): WorkerGeometry.GeometryData[] {
        return this.measureMeshBackedGeometry(() => this.meshesToGeometry(this.createDish(id, materialId, dishInfo)));
    }

    createIShapeGeometry(id: string, materialId: string, ishapeInfo: Data.GraphicData.IShape): WorkerGeometry.GeometryData {
        return this.measureMeshBackedGeometry(() => this.meshToGeometry(this.createIShape(id, materialId, ishapeInfo)));
    }

    createLShapeGeometry(id: string, materialId: string, lshapeInfo: Data.GraphicData.LShape): WorkerGeometry.GeometryData {
        return this.measureMeshBackedGeometry(() => this.meshToGeometry(this.createLShape(id, materialId, lshapeInfo)));
    }

    createTShapeGeometry(id: string, materialId: string, tshapeInfo: Data.GraphicData.TShape): WorkerGeometry.GeometryData {
        return this.measureMeshBackedGeometry(() => this.meshToGeometry(this.createTShape(id, materialId, tshapeInfo)));
    }

    createUShapeGeometry(id: string, materialId: string, ushapeInfo: Data.GraphicData.UShape): WorkerGeometry.GeometryData {
        return this.measureMeshBackedGeometry(() => this.meshToGeometry(this.createUShape(id, materialId, ushapeInfo)));
    }

    createRevolutionGeometry(id: string, materialId: string, revolutionInfo: Data.GraphicData.Revolution): WorkerGeometry.GeometryData[] {
        return this.measureMeshBackedGeometry(() => this.meshesToGeometry(this.createRevolution(id, materialId, revolutionInfo)));
    }

    createTorusGeometry(id: string, materialId: string, torusInfo: Data.GraphicData.Torus): WorkerGeometry.GeometryData[] {
        return this.measureMeshBackedGeometry(() => this.meshesToGeometry(this.createTorus(id, materialId, torusInfo)));
    }

    createRectTorusGeometry(id: string, materialId: string, rectTorusInfo: Data.GraphicData.RectTorus): WorkerGeometry.GeometryData[] {
        return this.measureMeshBackedGeometry(() => this.meshesToGeometry(this.createRectTorus(id, materialId, rectTorusInfo)));
    }

    createCylinderGeometry(id: string, materialId: string, cylinderInfo: Data.GraphicData.Cylinder): WorkerGeometry.GeometryData {
        return this.measureDirectGeometry(() => {
            cylinderInfo.validate();

            var complexityLevel = this.getCylinderComplexityLevel(cylinderInfo.Radius, 1.0);
            var scale = new BABYLON.Vector3(cylinderInfo.Radius * 2 / 1000, cylinderInfo.Length / 1000, cylinderInfo.Radius * 2 / 1000);
            var matrix = MeshFactory.composeTransformMatrix(scale, cylinderInfo.Position, cylinderInfo.Rotation);

            return WorkerGeometry.Utils.transform(this.getComplexityDefinedCylinderGeometry(complexityLevel), matrix, id, id);
        });
    }

    createConeGeometry(id: string, materialId: string, coneInfo: Data.GraphicData.Cone): WorkerGeometry.GeometryData {
        return this.measureMeshBackedGeometry(() => this.meshToGeometry(this.createCone(id, materialId, coneInfo)));
    }

    createEccentricConeGeometry(id: string, materialId: string, coneInfo: Data.GraphicData.Cone): WorkerGeometry.GeometryData[] {
        return this.measureMeshBackedGeometry(() => this.meshesToGeometry(this.createEccentricCone(id, materialId, coneInfo)));
    }

    createPolyhedronGeometry(id: string, materialId: string, polyhedronInfo: Data.GraphicData.Polyhedron): WorkerGeometry.GeometryData[] {
        return this.measureMeshBackedGeometry(() => this.meshesToGeometry(this.createPolyhedron(id, materialId, polyhedronInfo)));
    }

    createSphereGeometry(id: string, materialId: string, sphereInfo: Data.GraphicData.Sphere): WorkerGeometry.GeometryData {
        return this.measureDirectGeometry(() => {
            sphereInfo.validate();

            var level = this.getSphereComplexityLevel(sphereInfo.Radius);
            var diameter = sphereInfo.Radius * 2 / 1000;
            var scale = new BABYLON.Vector3(diameter, diameter, diameter);
            var matrix = MeshFactory.composeTransformMatrix(scale, sphereInfo.Position);

            return WorkerGeometry.Utils.transform(this.getComplexityDefinedSphereGeometry(level), matrix, id, id);
        });
    }

    createExtrusionGeometry(id: string, materialId: string, extrusionInfo: Data.GraphicData.Extrusion): WorkerGeometry.GeometryData[] {
        return this.measureMeshBackedGeometry(() => this.meshesToGeometry(this.createExtrusion(id, materialId, extrusionInfo)));
    }

    createPyramidGeometry(id: string, materialId: string, pyramidInfo: Data.GraphicData.Pyramid): WorkerGeometry.GeometryData {
        return this.measureMeshBackedGeometry(() => this.meshToGeometry(this.createPyramid(id, materialId, pyramidInfo)));
    }

    createShellGeometry(id: string, materialId: string, shellInfo: Data.GraphicData.Shell): WorkerGeometry.GeometryData[] {
        return this.measureMeshBackedGeometry(() => this.meshesToGeometry(this.createShell(id, materialId, shellInfo)));
    }

    createPolygonCollectionGeometry(id: string, materialId: string, polygonCollectionInfo: Data.GraphicData.PolygonCollection): WorkerGeometry.GeometryData[] {
        return this.measureMeshBackedGeometry(() => this.meshesToGeometry(this.createPolygonCollection(id, materialId, polygonCollectionInfo)));
    }

    createBox(id: string, materialId: string, boxInfo: Data.GraphicData.Box): BABYLON.Mesh {
        boxInfo.validate();
        var box: BABYLON.Mesh;

        box = this._boxMasterMesh.clone(id);

        box.scaling = new BABYLON.Vector3(boxInfo.Scale.Y / 1000, boxInfo.Scale.Z / 1000, boxInfo.Scale.X / 1000);
        box.id = id;
        box.rotate(new BABYLON.Vector3(boxInfo.Rotation.X, boxInfo.Rotation.Y, boxInfo.Rotation.Z), boxInfo.Rotation.W, BABYLON.Space.WORLD);
        box.position.x = boxInfo.Position.X / 1000;
        box.position.y = boxInfo.Position.Y / 1000;
        box.position.z = boxInfo.Position.Z / 1000;

        return box;
    }

    createDish(id: string, materialId: string, dishInfo: Data.GraphicData.Dish): BABYLON.Mesh[] {
        dishInfo.validate();

        var complexityLevel = this.getCylinderComplexityLevel(Math.max(dishInfo.Radius, dishInfo.Height), 1.0);

        var profile: BABYLON.Vector3[] = [];
        this.getEllipseContour(
            new BABYLON.Vector2(0, 0),
            MeshFactory.convertToLocalCoordinateSystem(dishInfo.Radius),
            MeshFactory.convertToLocalCoordinateSystem(dishInfo.Height),
            0.0,
            0,
            90,
            true,
            complexityLevel,
            false,
            profile
        );

        var startRadius = MeshFactory.convertToLocalCoordinateSystem(MeshFactory.ZERO_REVOLVE_RADIUS);

        var meshes = this.revolveContour("dish", profile, startRadius, complexityLevel, 1.0, 1.0, BABYLON.Mesh.NO_CAP, false, true, this._scene);

        // Dish uses a different up vector. Because we don't support specifying the up vector in revolveContour(),
        // we need to manually adjust for it here.
        for (var i = 0; i < meshes.length; i++) {
            meshes[i].rotation.z = -Math.PI / 2;
        }

        MeshFactory.setMeshesTransformation(meshes, dishInfo.Position, dishInfo.Rotation);

        return meshes;
    }
    
    createIShape(id: string, materialId: string, ishapeInfo: Data.GraphicData.IShape): BABYLON.Mesh {
        ishapeInfo.validate();

        var iShape = new BABYLON.Mesh(id, this._scene);

        var width = ishapeInfo.Scale.Y / 1000;
        var height = ishapeInfo.Scale.X / 1000;
        var length = ishapeInfo.Scale.Z / 1000;

        var centerThickness = ishapeInfo.CenterThickness / 1000;
        var endThickness = ishapeInfo.EndThickness / 1000;

        // Helper variables for iShape Geometry
        var halfWidth = width * 0.5;
        var halfHeight = height * 0.5;
        var halfCenterThickness = centerThickness * 0.5;

        var indices: any[] = [];
        var positions: any[] = [];
        var normals: any[] = [];

        // Generate points for Shape (12 points for each end)...
        // 
        //	5----------6		17--------18
        //  |		   |		|          |
        //  4---3  8---7        16-15  20-19
        //      |  |                |  |
        //	1---2  9--10		13-14  21-22
        //  |          |		|          |
        //  0---------11		12--------23

        // Front Face Points
        //0
        var p0 = new BABYLON.Vector3(-halfWidth, 0.0, -halfHeight);
        //1
        var p1 = new BABYLON.Vector3(-halfWidth, 0.0, -halfHeight + endThickness);
        //2
        var p2 = new BABYLON.Vector3(-halfCenterThickness, 0.0, -halfHeight + endThickness);
        //3
        var p3 = new BABYLON.Vector3(-halfCenterThickness, 0.0, halfHeight - endThickness);
        //4
        var p4 = new BABYLON.Vector3(-halfWidth, 0.0, halfHeight - endThickness);
        //5
        var p5 = new BABYLON.Vector3(-halfWidth, 0.0, halfHeight);
        //6
        var p6 = new BABYLON.Vector3(halfWidth, 0.0, halfHeight);
        //7
        var p7 = new BABYLON.Vector3(halfWidth, 0.0, halfHeight - endThickness);
        //8
        var p8 = new BABYLON.Vector3(halfCenterThickness, 0.0, halfHeight - endThickness);
        //9
        var p9 = new BABYLON.Vector3(halfCenterThickness, 0.0, -halfHeight + endThickness);
        //10
        var p10 = new BABYLON.Vector3(halfWidth, 0.0, -halfHeight + endThickness);
        //11
        var p11 = new BABYLON.Vector3(halfWidth, 0.0, -halfHeight);

        // Back face points
        //12
        var p12 = new BABYLON.Vector3(-halfWidth, length, -halfHeight);
        //13
        var p13 = new BABYLON.Vector3(-halfWidth, length, -halfHeight + endThickness);
        //14
        var p14 = new BABYLON.Vector3(-halfCenterThickness, length, -halfHeight + endThickness);
        //15
        var p15 = new BABYLON.Vector3(-halfCenterThickness, length, halfHeight - endThickness);
        //16
        var p16 = new BABYLON.Vector3(-halfWidth, length, halfHeight - endThickness);
        //17
        var p17 = new BABYLON.Vector3(-halfWidth, length, halfHeight);
        //18
        var p18 = new BABYLON.Vector3(halfWidth, length, halfHeight);
        //19
        var p19 = new BABYLON.Vector3(halfWidth, length, halfHeight - endThickness);
        //20
        var p20 = new BABYLON.Vector3(halfCenterThickness, length, halfHeight - endThickness);
        //21
        var p21 = new BABYLON.Vector3(halfCenterThickness, length, -halfHeight + endThickness);
        //22
        var p22 = new BABYLON.Vector3(halfWidth, length, -halfHeight + endThickness);
        //23
        var p23 = new BABYLON.Vector3(halfWidth, length, -halfHeight);

        // Front Face
        this.tesselateRectangle(p0, p1, p10, p11, new BABYLON.Vector3(0, -1, 0), indices, positions, normals);
        this.tesselateRectangle(p2, p3, p8, p9, new BABYLON.Vector3(0, -1, 0), indices, positions, normals);
        this.tesselateRectangle(p4, p5, p6, p7, new BABYLON.Vector3(0, -1, 0), indices, positions, normals);

        // Back Face
        this.tesselateRectangle(p23, p22, p13, p12, new BABYLON.Vector3(0, 1, 0), indices, positions, normals);
        this.tesselateRectangle(p21, p20, p15, p14, new BABYLON.Vector3(0, 1, 0), indices, positions, normals);
        this.tesselateRectangle(p19, p18, p17, p16, new BABYLON.Vector3(0, 1, 0), indices, positions, normals);

        // Left Facing Sides
        this.tesselateRectangle(p12, p13, p1, p0, new BABYLON.Vector3(-1, 0, 0), indices, positions, normals);
        this.tesselateRectangle(p14, p15, p3, p2, new BABYLON.Vector3(-1, 0, 0), indices, positions, normals);
        this.tesselateRectangle(p16, p17, p5, p4, new BABYLON.Vector3(-1, 0, 0), indices, positions, normals);

        // Right Facing Sides
        this.tesselateRectangle(p7, p6, p18, p19, new BABYLON.Vector3(1, 0, 0), indices, positions, normals);
        this.tesselateRectangle(p9, p8, p20, p21, new BABYLON.Vector3(1, 0, 0), indices, positions, normals);
        this.tesselateRectangle(p11, p10, p22, p23, new BABYLON.Vector3(1, 0, 0), indices, positions, normals);

        // Up Facing Sides
        this.tesselateRectangle(p2, p1, p13, p14, new BABYLON.Vector3(0, 0, 1), indices, positions, normals);
        this.tesselateRectangle(p6, p5, p17, p18, new BABYLON.Vector3(0, 0, 1), indices, positions, normals);
        this.tesselateRectangle(p10, p9, p21, p22, new BABYLON.Vector3(0, 0, 1), indices, positions, normals);

        // Down Facing Sides
        this.tesselateRectangle(p4, p3, p15, p16, new BABYLON.Vector3(0, 0, -1), indices, positions, normals);
        this.tesselateRectangle(p0, p11, p23, p12, new BABYLON.Vector3(0, 0, -1), indices, positions, normals);
        this.tesselateRectangle(p8, p7, p19, p20, new BABYLON.Vector3(0, 0, -1), indices, positions, normals);

        iShape.setVerticesData(BABYLON.VertexBuffer.PositionKind, positions, true);
        iShape.setVerticesData(BABYLON.VertexBuffer.NormalKind, normals, true);

        iShape.setIndices(indices);
        iShape.rotate(new BABYLON.Vector3(ishapeInfo.Rotation.X, ishapeInfo.Rotation.Y, ishapeInfo.Rotation.Z), ishapeInfo.Rotation.W, BABYLON.Space.WORLD);
        iShape.position.x = ishapeInfo.Position.X / 1000;
        iShape.position.y = ishapeInfo.Position.Y / 1000;
        iShape.position.z = ishapeInfo.Position.Z / 1000;
        iShape.id = id;

        return iShape;
    }

    createLShape(id: string, materialId: string, lshapeInfo: Data.GraphicData.LShape): BABYLON.Mesh {

        lshapeInfo.validate();

        var lShape = new BABYLON.Mesh(id, this._scene);

        var width = lshapeInfo.Scale.Y / 1000;
        var height = lshapeInfo.Scale.X / 1000;
        var length = lshapeInfo.Scale.Z / 1000;
        var thickness = lshapeInfo.Thickness / 1000;

        // Helper variables for lShape Geometry
        var halfWidth = width * 0.5;
        var halfHeight = height * 0.5;
        var halfCenterThickness = thickness * 0.5;

        var indices: any[] = [];
        var positions: any[] = [];
        var normals: any[] = [];

        // Generate points for Shape (6 points for each end)...
        // 
        //	1---2           7---8
        //	|   |           |   |
        //  |   |           |   |
        //	|   3-----4     |   9----10
        //  |         |		|         |
        //  0---------5		6--------11

        // Front Face Points
        //0
        var p0 = new BABYLON.Vector3(0.0, 0.0, 0.0);
        //1
        var p1 = new BABYLON.Vector3(0.0, 0.0, height);
        //2
        var p2 = new BABYLON.Vector3(thickness, 0.0, height);
        //3
        var p3 = new BABYLON.Vector3(thickness, 0.0, thickness);
        //4
        var p4 = new BABYLON.Vector3(width, 0.0, thickness);
        //5
        var p5 = new BABYLON.Vector3(width, 0.0, 0.0);

        // Back Face Points
        //6
        var p6 = new BABYLON.Vector3(0.0, length, 0.0);
        //7
        var p7 = new BABYLON.Vector3(0.0, length, height);
        //8
        var p8 = new BABYLON.Vector3(thickness, length, height);
        //9
        var p9 = new BABYLON.Vector3(thickness, length, thickness);
        //10
        var p10 = new BABYLON.Vector3(width, length, thickness);
        //11
        var p11 = new BABYLON.Vector3(width, length, 0.0);

        // Front Face
        this.tesselateRectangle(p0, p1, p2, p3, new BABYLON.Vector3(0, -1, 0), indices, positions, normals);
        this.tesselateRectangle(p0, p3, p4, p5, new BABYLON.Vector3(0, -1, 0), indices, positions, normals);

        // Back Face
        this.tesselateRectangle(p11, p10, p9, p6, new BABYLON.Vector3(0, 1, 0), indices, positions, normals);
        this.tesselateRectangle(p9, p8, p7, p6, new BABYLON.Vector3(0, 1, 0), indices, positions, normals);

        // Left Faces
        this.tesselateRectangle(p6, p7, p1, p0, new BABYLON.Vector3(-1, 0, 0), indices, positions, normals);
        //this.tesselateRectangle(p11, p10, p4, p5, new BABYLON.Vector3(-1, 0, 0), indices, positions, normals);

        // Right Faces
        this.tesselateRectangle(p5, p4, p10, p11, new BABYLON.Vector3(1, 0, 0), indices, positions, normals);
        this.tesselateRectangle(p3, p2, p8, p9, new BABYLON.Vector3(1, 0, 0), indices, positions, normals);

        // Up Faces
        this.tesselateRectangle(p2, p1, p7, p8, new BABYLON.Vector3(0, 0, 1), indices, positions, normals);
        this.tesselateRectangle(p4, p3, p9, p10, new BABYLON.Vector3(0, 0, 1), indices, positions, normals);

        // Down Faces
        this.tesselateRectangle(p0, p5, p11, p6, new BABYLON.Vector3(0, 0, -1), indices, positions, normals);

        lShape.setVerticesData(BABYLON.VertexBuffer.PositionKind, positions, true);
        lShape.setVerticesData(BABYLON.VertexBuffer.NormalKind, normals, true);
        lShape.setIndices(indices);
        lShape.rotate(new BABYLON.Vector3(lshapeInfo.Rotation.X, lshapeInfo.Rotation.Y, lshapeInfo.Rotation.Z), lshapeInfo.Rotation.W, BABYLON.Space.WORLD);
        lShape.position.x = lshapeInfo.Position.X / 1000;
        lShape.position.y = lshapeInfo.Position.Y / 1000;
        lShape.position.z = lshapeInfo.Position.Z / 1000;
        lShape.id = id;

        return lShape;
    }

    createTShape(id: string, materialId: string, tshapeInfo: Data.GraphicData.TShape): BABYLON.Mesh {
        tshapeInfo.validate();
        var tShape = new BABYLON.Mesh(id, this._scene);

        var width = tshapeInfo.Scale.Y / 1000;
        var height = tshapeInfo.Scale.X / 1000;
        var length = tshapeInfo.Scale.Z / 1000;
        var flangeThickness = tshapeInfo.TopThickness / 1000;
        var webThickness = tshapeInfo.CenterThickness / 1000;

        // Helper variables for uShape Geometry
        var halfWidth = width * 0.5;
        var halfHeight = height * 0.5;

        var indices: any[] = [];
        var positions: any[] = [];
        var normals: any[] = [];
        var halfWidth = width * 0.5;
        var halfHeight = height * 0.5;
        var halfCenterThickness = webThickness * 0.5;
        
        // Generate points for shape (8 points for each end)...
        // 
        //	3----------4		11--------12
        //  |		   |		|          |
        //  2---1  6---5        10--9  14-13
        //      |  |                |  |
        //      |  |                |  |
        //      |  |                |  |
        //	    0--7		        8-15

        // Front Face Points
        //0
        var p0 = new BABYLON.Vector3(-halfCenterThickness, 0.0, -halfHeight);
        //1
        var p1 = new BABYLON.Vector3(-halfCenterThickness, 0.0, halfHeight - flangeThickness);
        //2
        var p2 = new BABYLON.Vector3(-halfWidth, 0.0, halfHeight - flangeThickness);
        //3
        var p3 = new BABYLON.Vector3(-halfWidth, 0.0, halfHeight);
        //4
        var p4 = new BABYLON.Vector3(halfWidth, 0.0, halfHeight);
        //5
        var p5 = new BABYLON.Vector3(halfWidth, 0.0, halfHeight - flangeThickness);
        //6
        var p6 = new BABYLON.Vector3(halfCenterThickness, 0.0, halfHeight - flangeThickness);
        //7
        var p7 = new BABYLON.Vector3(halfCenterThickness, 0.0, -halfHeight);

        // Back Face Points
        //8
        var p8 = new BABYLON.Vector3(-halfCenterThickness, length, -halfHeight);
        //9
        var p9 = new BABYLON.Vector3(-halfCenterThickness, length, halfHeight - flangeThickness);
        //10
        var p10 = new BABYLON.Vector3(-halfWidth, length, halfHeight - flangeThickness);
        //11
        var p11 = new BABYLON.Vector3(-halfWidth, length, halfHeight);

        //12
        var p12 = new BABYLON.Vector3(halfWidth, length, halfHeight);
        //13
        var p13 = new BABYLON.Vector3(halfWidth, length, halfHeight - flangeThickness);
        //14
        var p14 = new BABYLON.Vector3(halfCenterThickness, length, halfHeight - flangeThickness);
        //15
        var p15 = new BABYLON.Vector3(halfCenterThickness, length, -halfHeight);

        // Front Face
        this.tesselateRectangle(p2, p3, p4, p5, new BABYLON.Vector3(0, -1, 0), indices, positions, normals);
        this.tesselateRectangle(p0, p1, p6, p7, new BABYLON.Vector3(0, -1, 0), indices, positions, normals);

        // Back Face
        this.tesselateRectangle(p15, p14, p9, p8, new BABYLON.Vector3(0, 1, 0), indices, positions, normals);
        this.tesselateRectangle(p13, p12, p11, p10, new BABYLON.Vector3(0, 1, 0), indices, positions, normals);

        // Left Facing Sides
        this.tesselateRectangle(p8, p9, p1, p0, new BABYLON.Vector3(-1, 0, 0), indices, positions, normals);
        this.tesselateRectangle(p10, p11, p3, p2, new BABYLON.Vector3(-1, 0, 0), indices, positions, normals);

        this.tesselateRectangle(p5, p4, p12, p13, new BABYLON.Vector3(1, 0, 0), indices, positions, normals);
        this.tesselateRectangle(p7, p6, p14, p15, new BABYLON.Vector3(1, 0, 0), indices, positions, normals);

        this.tesselateRectangle(p4, p3, p11, p12, new BABYLON.Vector3(0, 0, 1), indices, positions, normals);

        this.tesselateRectangle(p2, p1, p9, p10, new BABYLON.Vector3(0, 0, -1), indices, positions, normals);
        this.tesselateRectangle(p6, p5, p13, p14, new BABYLON.Vector3(0, 0, -1), indices, positions, normals);
        this.tesselateRectangle(p0, p7, p15, p8, new BABYLON.Vector3(0, 0, -1), indices, positions, normals);

        tShape.setVerticesData(BABYLON.VertexBuffer.PositionKind, positions, true);
        tShape.setVerticesData(BABYLON.VertexBuffer.NormalKind, normals, true);
        tShape.setIndices(indices);
        tShape.rotate(new BABYLON.Vector3(tshapeInfo.Rotation.X, tshapeInfo.Rotation.Y, tshapeInfo.Rotation.Z), tshapeInfo.Rotation.W, BABYLON.Space.WORLD);
        tShape.position.x = tshapeInfo.Position.X / 1000;
        tShape.position.y = tshapeInfo.Position.Y / 1000;
        tShape.position.z = tshapeInfo.Position.Z / 1000;
        tShape.id = id;

        return tShape;
    }

    createUShape(id: string, materialId: string, ushapeInfo: Data.GraphicData.UShape): BABYLON.Mesh {
        ushapeInfo.validate();

        var uShape = new BABYLON.Mesh(id, this._scene);

        var width = ushapeInfo.Scale.Y / 1000;
        var height = ushapeInfo.Scale.X / 1000;
        var length = ushapeInfo.Scale.Z / 1000;
        var flangeThickness = ushapeInfo.FlangeThickness / 1000;
        var webThickness = ushapeInfo.WebThickness / 1000;

        // Helper variables for uShape Geometry
        var halfWidth = width * 0.5;
        var halfHeight = height * 0.5;

        var indices: any[] = [];
        var positions: any[] = [];
        var normals: any[] = [];

        // Generate points for Shape (8 points for each end)...
        // 
        //	1----------2		9---------10
        //  |		   |		|          |
        //  |   4------3        |  12-----11
        //  |   |               |   |
        //	|   5------6		|  13-----14
        //  |          |		|          |
        //  0----------7		8---------15

        // Front Face Points
        //0
        var p0 = new BABYLON.Vector3(-halfWidth, 0.0, -halfHeight);
        //1
        var p1 = new BABYLON.Vector3(-halfWidth, 0.0, halfHeight);
        //2
        var p2 = new BABYLON.Vector3(halfWidth, 0.0, halfHeight);
        //3
        var p3 = new BABYLON.Vector3(halfWidth, 0.0, halfHeight - flangeThickness);
        //4
        var p4 = new BABYLON.Vector3(-halfWidth + webThickness, 0.0, halfHeight - flangeThickness);
        //5
        var p5 = new BABYLON.Vector3(-halfWidth + webThickness, 0.0, -halfHeight + flangeThickness);
        //6
        var p6 = new BABYLON.Vector3(halfWidth, 0.0, -halfHeight + flangeThickness);
        //7
        var p7 = new BABYLON.Vector3(halfWidth, 0.0, -halfHeight);

        // Back Face Points
        //8
        var p8 = new BABYLON.Vector3(-halfWidth, length, -halfHeight);
        //9
        var p9 = new BABYLON.Vector3(-halfWidth, length, halfHeight);
        //10
        var p10 = new BABYLON.Vector3(halfWidth, length, halfHeight);
        //11
        var p11 = new BABYLON.Vector3(halfWidth, length, halfHeight - flangeThickness);

        //12
        var p12 = new BABYLON.Vector3(-halfWidth + webThickness, length, halfHeight - flangeThickness);
        //13
        var p13 = new BABYLON.Vector3(-halfWidth + webThickness, length, -halfHeight + flangeThickness);
        //14
        var p14 = new BABYLON.Vector3(halfWidth, length, -halfHeight + flangeThickness);
        //15
        var p15 = new BABYLON.Vector3(halfWidth, length, -halfHeight);

        // Front Face
        this.tesselateRectangle(p0, p1, p4, p5, new BABYLON.Vector3(0, -1, 0), indices, positions, normals);
        this.tesselateRectangle(p1, p2, p3, p4, new BABYLON.Vector3(0, -1, 0), indices, positions, normals);
        this.tesselateRectangle(p0, p5, p6, p7, new BABYLON.Vector3(0, -1, 0), indices, positions, normals);

        // Back Face
        this.tesselateRectangle(p13, p12, p9, p8, new BABYLON.Vector3(0, 1, 0), indices, positions, normals);
        this.tesselateRectangle(p8, p15, p14, p13, new BABYLON.Vector3(0, 1, 0), indices, positions, normals);
        this.tesselateRectangle(p12, p11, p10, p9, new BABYLON.Vector3(0, 1, 0), indices, positions, normals);

        // Left Facing Sides
        this.tesselateRectangle(p8, p9, p1, p0, new BABYLON.Vector3(-1, 0, 0), indices, positions, normals);

        // Right Facing Sides
        this.tesselateRectangle(p3, p2, p10, p11, new BABYLON.Vector3(1, 0, 0), indices, positions, normals);
        this.tesselateRectangle(p5, p4, p12, p13, new BABYLON.Vector3(1, 0, 0), indices, positions, normals);
        this.tesselateRectangle(p7, p6, p14, p15, new BABYLON.Vector3(1, 0, 0), indices, positions, normals);

        // Up Facing Sides
        this.tesselateRectangle(p2, p1, p9, p10, new BABYLON.Vector3(0, 0, 1), indices, positions, normals);
        this.tesselateRectangle(p6, p5, p13, p14, new BABYLON.Vector3(0, 0, 1), indices, positions, normals);

        // Down Facing Sides
        this.tesselateRectangle(p0, p7, p15, p8, new BABYLON.Vector3(0, 0, -1), indices, positions, normals);
        this.tesselateRectangle(p4, p3, p11, p12, new BABYLON.Vector3(0, 0, -1), indices, positions, normals);

        uShape.setVerticesData(BABYLON.VertexBuffer.PositionKind, positions, true);
        uShape.setVerticesData(BABYLON.VertexBuffer.NormalKind, normals, true);
        uShape.setIndices(indices);
        uShape.rotate(new BABYLON.Vector3(ushapeInfo.Rotation.X, ushapeInfo.Rotation.Y, ushapeInfo.Rotation.Z), ushapeInfo.Rotation.W, BABYLON.Space.WORLD);
        uShape.position.x = ushapeInfo.Position.X / 1000;
        uShape.position.y = ushapeInfo.Position.Y / 1000;
        uShape.position.z = ushapeInfo.Position.Z / 1000;
        uShape.id = id;

        return uShape;
    }

    private revolveContour(name: string, shape: BABYLON.Vector3[], startRadius: number, tessellation: number, arc: number, scale: number, cap: number, flatShaded: boolean, clockwise: boolean, scene: BABYLON.Scene): BABYLON.Mesh[] {
        var numShapePoints = shape.length;
        var closed = (arc >= 1.0);
        var contourClosed = false;

        if (arc > 1.0) arc = 1.0;
        if (closed) cap = BABYLON.Mesh.NO_CAP;

        var pi2 = Math.PI * 2;
        var paths = new Array();

        var i = 0;
        var p = 0;
        var endAngle = pi2 * arc;
        var step = endAngle / tessellation;
        var rotated: BABYLON.Vector3;
        var path = new Array<BABYLON.Vector3>();

        var scaleStep = (scale - 1.0) / tessellation;

        if (closed) tessellation -= 1;

        if (shape[0].equalsWithEpsilon(shape[numShapePoints - 1], MeshFactory.DISTANCE_TOLERANCE)) {
            numShapePoints -= 1;
            contourClosed = true;
        }

        for (i = 0; i <= tessellation; i++) {
            var path: BABYLON.Vector3[] = [];

            if (clockwise) {
                for (p = 0; p < numShapePoints; p++) {
                    rotated = new BABYLON.Vector3(
                        Math.cos(i * step) * (shape[p].x + (shape[p].x * i * scaleStep) + startRadius) - startRadius,
                        shape[p].y + (shape[p].y * i * scaleStep),
                        Math.sin(i * step) * (shape[p].x + (shape[p].x * i * scaleStep) + startRadius)
                    );

                    path.push(rotated);
                    if (flatShaded)
                        path.push(rotated);
                }
            }
            else {
                for (p = (numShapePoints - 1); p >= 0; p--) {
                    rotated = new BABYLON.Vector3(
                        Math.cos(i * step) * (shape[p].x + (shape[p].x * i * scaleStep) + startRadius) - startRadius,
                        shape[p].y + (shape[p].y * i * scaleStep),
                        Math.sin(i * step) * (shape[p].x + (shape[p].x * i * scaleStep) + startRadius)
                    );

                    path.push(rotated);
                    if (flatShaded)
                        path.push(rotated);
                }
            }

            paths.push(path);
        }

        var lathe = BABYLON.MeshBuilder.CreateRibbon(name, { pathArray: paths, closePath: contourClosed, closeArray: closed, sideOrientation: BABYLON.Mesh.DEFAULTSIDE, updatable: false }, scene);

        var resultMeshes: BABYLON.Mesh[] = [lathe];

        if (cap === BABYLON.Mesh.CAP_START || cap === BABYLON.Mesh.CAP_ALL) {

            try {
                var capPoints: BABYLON.Vector2[] = [];

                if (clockwise) {
                    for (p = 0; p < numShapePoints; p++) {
                        capPoints.push(new BABYLON.Vector2(shape[p].x, shape[p].y));
                    }
                }
                else {
                    for (p = (numShapePoints - 1); p >= 0; p--) {
                        capPoints.push(new BABYLON.Vector2(shape[p].x, shape[p].y));
                    }
                }

                var capBuilder = new BABYLON.PolygonMeshBuilder("cap", capPoints, scene);
                var capMesh = capBuilder.build();

                capMesh.rotate(new BABYLON.Vector3(1, 0, 0), -Math.PI / 2, BABYLON.Space.WORLD);
                resultMeshes.push(capMesh);
            }
            catch (error) {
                console.log("Error generating cap for revolution", {
                    meshName: name,
                    cap: "start",
                    error: error instanceof Error ? error.message : String(error)
                });
            }
        }

        if (cap == BABYLON.Mesh.CAP_END || cap == BABYLON.Mesh.CAP_ALL) {
            try {
                var capPoints: BABYLON.Vector2[] = [];

                if (clockwise) {
                    for (p = 0; p < numShapePoints; p++) {
                        capPoints.push(new BABYLON.Vector2(shape[p].x * scale + startRadius, -shape[p].y * scale));
                    }
                }
                else {
                    for (p = (numShapePoints - 1); p >= 0; p--) {
                        capPoints.push(new BABYLON.Vector2(shape[p].x * scale + startRadius, -shape[p].y * scale));
                    }
                }

                var capBuilder = new BABYLON.PolygonMeshBuilder("cap", capPoints, scene);
                var capMesh = capBuilder.build();

                capMesh.rotate(new BABYLON.Vector3(1, 0, 0), Math.PI / 2, BABYLON.Space.WORLD);
                capMesh.rotate(new BABYLON.Vector3(0, 1, 0), -endAngle, BABYLON.Space.WORLD);
                capMesh.translate(new BABYLON.Vector3(1, 0, 0), -startRadius, BABYLON.Space.WORLD);

                capMesh.bakeCurrentTransformIntoVertices();

                resultMeshes.push(capMesh);
            }
            catch (error) {
                console.log("Error generating cap for revolution", {
                    meshName: name,
                    cap: "end",
                    error: error instanceof Error ? error.message : String(error)
                });
            }
        }

        return resultMeshes;
    }

    createRevolution(id: string, materialId: string, revolutionInfo: Data.GraphicData.Revolution): BABYLON.Mesh[] {
        revolutionInfo.validate();

        var profile: BABYLON.Vector3[] = [];
        var flatShaded: boolean;

        for (var i = 0; i < revolutionInfo.NumCurves; i++) {
            var curve = revolutionInfo.GetCurve(i);

            if (curve instanceof Data.GraphicData.PolylineCurve) {
                var srcPoints = (curve as Data.GraphicData.PolylineCurve).PointSet;
                var pointsMax = 0.0;

                for (var n = 0; n < srcPoints.length; n++) {
                    profile.push(new BABYLON.Vector3(
                        MeshFactory.convertToLocalCoordinateSystem(srcPoints[n].X),
                        MeshFactory.convertToLocalCoordinateSystem(srcPoints[n].Y),
                        0.0));

                    pointsMax = Math.max(pointsMax, Math.abs(srcPoints[n].X));
                }

                profileSlices = this.getCylinderComplexityLevel(pointsMax, 1.0);
                flatShaded = true;
            }
            else {
                var srcCurve = curve as Data.GraphicData.EllipseCurve;

                var clockwise = (srcCurve.Clockwise === 1);
                var profileSlices = this.getCylinderComplexityLevel(Math.max(srcCurve.XRadius, srcCurve.YRadius), 1.0);

                this.getEllipseContour(
                    new BABYLON.Vector2(MeshFactory.convertToLocalCoordinateSystem(srcCurve.XCenter), MeshFactory.convertToLocalCoordinateSystem(srcCurve.YCenter)),
                    MeshFactory.convertToLocalCoordinateSystem(srcCurve.XRadius),
                    MeshFactory.convertToLocalCoordinateSystem(srcCurve.YRadius),
                    0.0,
                    srcCurve.StartAngle,
                    srcCurve.EndAngle,
                    !clockwise,
                    profileSlices,
                    false,
                    profile
                );

                flatShaded = false;
            }
        }

        var startRadius = MeshFactory.convertToLocalCoordinateSystem(revolutionInfo.SweepRadius);
        var arc = revolutionInfo.SweepAngle / 360.0;
        var cap = (revolutionInfo.Capped) ? BABYLON.Mesh.CAP_ALL : BABYLON.Mesh.NO_CAP;
        var clockwise = (revolutionInfo.Clockwise === 1);
        var tessellationSegments: number;

        if (revolutionInfo.SweepRadius > MeshFactory.ZERO_REVOLVE_RADIUS) {
            tessellationSegments = this.getCylinderComplexityLevel(revolutionInfo.SweepRadius, 1.0) + 1;
        }
        else {
            tessellationSegments = profileSlices + 1;
        }

        tessellationSegments = Math.floor(tessellationSegments * arc) + 1;
        if (tessellationSegments < 2) tessellationSegments = 2;

        var meshes = this.revolveContour("lathe", profile, startRadius, tessellationSegments, arc, 1.0, cap, flatShaded, clockwise, this._scene);
        MeshFactory.setMeshesTransformation(meshes, revolutionInfo.Position, revolutionInfo.Rotation);

        return meshes;
    }

    createTorus(id: string, materialId: string, torusInfo: Data.GraphicData.Torus): BABYLON.Mesh[] {

        var profile: BABYLON.Vector3[] = [];
        var profileSlices = this.getCylinderComplexityLevel(torusInfo.Radius, 1.0);

        this.getEllipseContour(
            new BABYLON.Vector2(0, 0),
            MeshFactory.convertToLocalCoordinateSystem(torusInfo.Radius),
            MeshFactory.convertToLocalCoordinateSystem(torusInfo.Radius),
            0.0,
            0,
            360,
            true,
            profileSlices,
            false,
            profile);

        var sweepRadius = (torusInfo.BendRadius < MeshFactory.ZERO_REVOLVE_RADIUS) ? MeshFactory.ZERO_REVOLVE_RADIUS : torusInfo.BendRadius;

        var startRadius = MeshFactory.convertToLocalCoordinateSystem(sweepRadius);
        var arc = (torusInfo.SweepAngle / 360.0);
        var scale = torusInfo.Scale;
        var cap = (torusInfo.Capped) ? BABYLON.Mesh.CAP_ALL : BABYLON.Mesh.NO_CAP;
        var clockwise = true;
        var flatShaded = false;
        var tessellationSegments: number;

        if (sweepRadius > MeshFactory.ZERO_REVOLVE_RADIUS) {
            tessellationSegments = this.getCylinderComplexityLevel(sweepRadius, 1.0) + 1;
        }
        else {
            tessellationSegments = profileSlices + 1;
        }

        tessellationSegments = Math.floor(tessellationSegments * arc) + 1;
        if (tessellationSegments < 2) tessellationSegments = 2;

        var meshes = this.revolveContour("torus", profile, startRadius, tessellationSegments, arc, scale, cap, flatShaded, clockwise, this._scene);
        MeshFactory.setMeshesTransformation(meshes, torusInfo.Position, torusInfo.Rotation);

        return meshes;
    }

    createRectTorus(id: string, materialId: string, rectTorusInfo: Data.GraphicData.RectTorus): BABYLON.Mesh[] {

        var halfWidth = MeshFactory.convertToLocalCoordinateSystem(rectTorusInfo.Height) / 2.0;
        var halfHeight = MeshFactory.convertToLocalCoordinateSystem(rectTorusInfo.Width) / 2.0;

        // Generate the points for the contour...
        var profile = [
            new BABYLON.Vector3(halfWidth, halfHeight, 0.0),
            new BABYLON.Vector3(halfWidth, -halfHeight, 0.0),
            new BABYLON.Vector3(-halfWidth, -halfHeight, 0.0),
            new BABYLON.Vector3(-halfWidth, halfHeight, 0.0),
            new BABYLON.Vector3(halfWidth, halfHeight, 0.0)
        ];

        var startRadius = MeshFactory.convertToLocalCoordinateSystem(rectTorusInfo.BendRadius);
        var arc = (rectTorusInfo.SweepAngle / 360.0);
        var cap = (rectTorusInfo.Capped) ? BABYLON.Mesh.CAP_ALL : BABYLON.Mesh.NO_CAP;
        var clockwise = false;
        var flatShaded = true;
        var tessellationSegments: number;

        if (rectTorusInfo.BendRadius > MeshFactory.ZERO_REVOLVE_RADIUS) {
            tessellationSegments = this.getCylinderComplexityLevel(rectTorusInfo.BendRadius, 1.0);
        }
        else {
            tessellationSegments = this.getCylinderComplexityLevel(rectTorusInfo.Height / 2.0, 1.0);
        }

        tessellationSegments = Math.floor(tessellationSegments * arc) + 1;
        if (tessellationSegments < 2) tessellationSegments = 2;

        var meshes = this.revolveContour("rtorus", profile, startRadius, tessellationSegments, arc, 1.0, cap, flatShaded, clockwise, this._scene);
        MeshFactory.setMeshesTransformation(meshes, rectTorusInfo.Position, rectTorusInfo.Rotation);

        return meshes;
    }

    private getEllipseContour(center: BABYLON.Vector2, xRadius: number, yRadius: number, zValue: number, startAngle: number, endAngle: number, ccDirection: boolean, nPolygonGenSlices: number, omitLastPoint: boolean, outProfile: BABYLON.Vector3[]) {

        var center3 = new BABYLON.Vector3(center.x, center.y, zValue);

        var dStartAngle: number, dTotalAngle: number, dAnglePerSlice: number, dDirection: number;
        var nGenPoints: number;

        // Set the direction and calculate the total angle based on the direction...
        if (ccDirection) {
            dDirection = 1.0;
            dTotalAngle = endAngle - startAngle;
        }
        else {
            dDirection = -1.0;
            dTotalAngle = startAngle - endAngle;
        }

        // Angles greater than 360 should be converted to lie in the range 0-360...
        if (Math.abs(dTotalAngle) > 360.0)
            dTotalAngle = dTotalAngle - Math.floor(dTotalAngle / 360.0) * 360.0;

        if (dTotalAngle > -360.0) {
            // Convert negative angles into positive angles, 
            // i.e. -90 degrees becomes +270 degrees...
            if (dTotalAngle < 0.0)
                dTotalAngle = 360.0 + dTotalAngle;
            // A zero angle is interpreted as 360 degrees.
            else if (Math.abs(dTotalAngle) < 1e-4)
                dTotalAngle = 360.0;
        }
        else
            // Can only reach here if angle is -360 degrees...
            dTotalAngle = -dTotalAngle;

        // Convert angle from degrees to radians...
        dTotalAngle = this.degToRad(dTotalAngle);
        dStartAngle = this.degToRad(startAngle);

        // Rather than dividing an ellipse's angle into a fixed number of slices, 
        // a complete ellipse is used to calculate the angle size. This results 
        // in equal segment size regardless of the total angle of an ellipse...
        dAnglePerSlice = (2.0 * Math.PI) / nPolygonGenSlices;

        // Calculate the number of points to generate...
        nGenPoints = Math.ceil(dTotalAngle / dAnglePerSlice);

        // Only generate ellipse if valid number of points...
        if (nGenPoints <= 0)
            return;

        // Calculate start point of ellipse...
        var newStartPoint = new BABYLON.Vector3(xRadius * Math.cos(dStartAngle), yRadius * Math.sin(dStartAngle), zValue);

        if (outProfile.length > 0) {
            // Check if the start point of the ellipse is equal to the end point
            // of the current contour. If they are equal the first ellipse point
            // can be ignored...
            if (!outProfile[outProfile.length - 1].equalsWithEpsilon(newStartPoint.add(center3), MeshFactory.DISTANCE_TOLERANCE)) {
                // Set first point (calculated previously)...
                outProfile.push(newStartPoint.add(center3));
            }
        }
        else {
            outProfile.push(newStartPoint.add(center3));
        }

        var lastPoint = newStartPoint;

        // Pre-compute some values...
        var cAng = Math.cos(dAnglePerSlice);
        var sAng = dDirection * Math.sin(dAnglePerSlice);
        var sxyRadius = (yRadius == 0) ? 0 : (xRadius / yRadius) * sAng;
        var syxRadius = (xRadius == 0) ? 0 : (yRadius / xRadius) * sAng;

        // Generate ellipse points (except last one), around the origin...
        for (var iPoint = 1; iPoint < nGenPoints; iPoint++) {
            lastPoint = new BABYLON.Vector3(
                lastPoint.x * cAng - sxyRadius * lastPoint.y,
                lastPoint.y * cAng + syxRadius * lastPoint.x,
                zValue
            );

            outProfile.push(lastPoint.add(center3));
        }

        if (!omitLastPoint) {
            // Calculate angle of last point...
            var dEndSliceAngle = dStartAngle + dDirection * dTotalAngle;

            // Generate the last point...
            lastPoint = new BABYLON.Vector3(
                xRadius * Math.cos(dEndSliceAngle),
                yRadius * Math.sin(dEndSliceAngle),
                zValue);

            outProfile.push(lastPoint.add(center3));
        }
    }

    private radToDeg(radangle: number): number {
        return (radangle * Math.PI) / 180.0;
    }

    // Convert an angle in degrees to an angle in radians...
    private degToRad(degangle: number): number {
        return (degangle / 180) * Math.PI;
    }

    private static convertToLocalCoordinateSystem(value: number): number {
        return value / 1000.0;
    }

    private static convertToLocalVector3(vector: Data.GraphicData.Vector3D): BABYLON.Vector3 {
        return new BABYLON.Vector3(
            MeshFactory.convertToLocalCoordinateSystem(vector.X),
            MeshFactory.convertToLocalCoordinateSystem(vector.Y),
            MeshFactory.convertToLocalCoordinateSystem(vector.Z)
        );
    }

    private static setMeshesTransformation(meshes: BABYLON.Mesh[], position: Data.GraphicData.Vector3D, rotation?: Data.GraphicData.Rotation) {
        for (var i = 0; i < meshes.length; i++) {
            MeshFactory.setMeshTransformation(meshes[i], position, rotation);
        }
    }

    private meshToGeometry(mesh: BABYLON.Mesh): WorkerGeometry.GeometryData {
        return WorkerGeometry.Utils.fromMesh(mesh, true);
    }

    private meshesToGeometry(meshes: BABYLON.Mesh[]): WorkerGeometry.GeometryData[] {
        return WorkerGeometry.Utils.fromMeshes(meshes, true);
    }

    private static composeTransformMatrix(scale: BABYLON.Vector3, position: Data.GraphicData.Vector3D, rotation?: Data.GraphicData.Rotation): BABYLON.Matrix {
        return BABYLON.Matrix.Compose(scale, MeshFactory.toQuaternion(rotation), MeshFactory.convertToLocalVector3(position));
    }

    private static toQuaternion(rotation?: Data.GraphicData.Rotation): BABYLON.Quaternion {
        if (rotation == null) {
            return BABYLON.Quaternion.Identity();
        }

        var axis = new BABYLON.Vector3(rotation.X, rotation.Y, rotation.Z);

        if (axis.lengthSquared() < MeshFactory.Epsilon || Math.abs(rotation.W) < MeshFactory.Epsilon) {
            return BABYLON.Quaternion.Identity();
        }

        axis.normalize();

        return BABYLON.Quaternion.RotationAxis(axis, rotation.W);
    }

    private static setMeshTransformation(mesh: BABYLON.Mesh, position: Data.GraphicData.Vector3D, rotation?: Data.GraphicData.Rotation) {
        if (rotation) {
            mesh.rotate(new BABYLON.Vector3(rotation.X, rotation.Y, rotation.Z), rotation.W, BABYLON.Space.WORLD);
        }

        mesh.position.x += MeshFactory.convertToLocalCoordinateSystem(position.X);
        mesh.position.y += MeshFactory.convertToLocalCoordinateSystem(position.Y);
        mesh.position.z += MeshFactory.convertToLocalCoordinateSystem(position.Z);
    }

    createCylinder(id: string, materialId: string, cylinderInfo: Data.GraphicData.Cylinder): BABYLON.Mesh {

       
        
            cylinderInfo.validate();

            var complexityLevel = this.getCylinderComplexityLevel(cylinderInfo.Radius, 1.0);

            var cylinder = this.getComplexityDefinedCylinderMesh(complexityLevel).clone(id);


            cylinder.scaling = new BABYLON.Vector3(cylinderInfo.Radius * 2 / 1000, cylinderInfo.Length / 1000, cylinderInfo.Radius * 2 / 1000);
            cylinder.rotate(new BABYLON.Vector3(cylinderInfo.Rotation.X, cylinderInfo.Rotation.Y, cylinderInfo.Rotation.Z), cylinderInfo.Rotation.W, BABYLON.Space.WORLD);

            cylinder.position.x = cylinderInfo.Position.X / 1000;
            cylinder.position.y = cylinderInfo.Position.Y / 1000;
            cylinder.position.z = cylinderInfo.Position.Z / 1000;

            return cylinder;
        
    }
    createInstancedCylinder(id: string, materialId: string, cylinderInfo: Data.GraphicData.Cylinder): BABYLON.InstancedMesh {

        //cylinderInfo.validate();

        var complexityLevel = this.getCylinderComplexityLevel(cylinderInfo.Radius, 1.0);

        var cylinder = this.getComplexityDefinedCylinderMesh(complexityLevel).createInstance(id);
        // This is left as sample code for better processing, but worse FPS. In an upcoming release of BABYLON, merging of instances will be supported,
        // and creating instances can be used instead of clones.

        cylinder.scaling = new BABYLON.Vector3(cylinderInfo.Radius * 2 / 1000, cylinderInfo.Length / 1000, cylinderInfo.Radius * 2 / 1000);
        cylinder.id = id;
        cylinder.rotate(new BABYLON.Vector3(cylinderInfo.Rotation.X, cylinderInfo.Rotation.Y, cylinderInfo.Rotation.Z), cylinderInfo.Rotation.W, BABYLON.Space.WORLD);
        cylinder.position.x = cylinderInfo.Position.X / 1000;
        cylinder.position.y = cylinderInfo.Position.Y / 1000;
        cylinder.position.z = cylinderInfo.Position.Z / 1000;

        return cylinder;
    }
    createCylinderInstanceTransform(cylinderInfo: Data.GraphicData.Cylinder): Data.GraphicData.CylinderInstanceTransform {

        var complexityLevel = this.getCylinderComplexityLevel(cylinderInfo.Radius, 1.0);

        return {
            PrimitiveType: "cylinder",
            ComplexityLevel: complexityLevel,
            Position: {
                X: cylinderInfo.Position.X / 1000,
                Y: cylinderInfo.Position.Y / 1000,
                Z: cylinderInfo.Position.Z / 1000
            },
            Scaling: {
                X: cylinderInfo.Radius * 2 / 1000,
                Y: cylinderInfo.Length / 1000,
                Z: cylinderInfo.Radius * 2 / 1000
            },
            Rotation: {
                X: cylinderInfo.Rotation.X,
                Y: cylinderInfo.Rotation.Y,
                Z: cylinderInfo.Rotation.Z,
                W: cylinderInfo.Rotation.W
            }
        };
    }
    createCone(id: string, materialId: string, coneInfo: Data.GraphicData.Cone): BABYLON.Mesh {
        coneInfo.validate();

        var complexityLevel = this.getCylinderComplexityLevel(Math.max(coneInfo.StartRadius, coneInfo.EndRadius), 1.0);

        var height = MeshFactory.convertToLocalCoordinateSystem(coneInfo.Height);
        var startDiameter = MeshFactory.convertToLocalCoordinateSystem(coneInfo.StartRadius * 2);
        var endDiameter = MeshFactory.convertToLocalCoordinateSystem(coneInfo.EndRadius * 2);

        var flipSides = false;

        if (endDiameter == 0) {
            var tempVar = endDiameter;
            endDiameter = startDiameter;
            startDiameter = tempVar;

            flipSides = true;
        }

        // To Do (soon). Scaling "should" work on cones just the same, as long as we singleton'ify on start/end radius (like JD's suggested "catalog").
        var cone = BABYLON.Mesh.CreateCylinder(id, height, startDiameter, endDiameter, complexityLevel, 1, this._scene);

        if (flipSides) {
            cone.rotate(MeshFactory.XAXIS, Math.PI, BABYLON.Space.LOCAL);
        }

        MeshFactory.setMeshTransformation(cone, coneInfo.Position, coneInfo.Rotation);
                
        return cone;
    }

    createEccentricCone(id: string, materialId: string, coneInfo: Data.GraphicData.Cone): BABYLON.Mesh[] {
        coneInfo.validate();

        var complexityLevel = this.getCylinderComplexityLevel(Math.max(coneInfo.StartRadius, coneInfo.EndRadius), 1.0);

        var startProfile: BABYLON.Vector3[] = [];
        this.getEllipseContour(
            new BABYLON.Vector2(0, 0),
            MeshFactory.convertToLocalCoordinateSystem(coneInfo.StartRadius),
            MeshFactory.convertToLocalCoordinateSystem(coneInfo.StartRadius),
            -MeshFactory.convertToLocalCoordinateSystem(coneInfo.Height / 4),
            0,
            360,
            true,
            complexityLevel,
            true,
            startProfile
        );

        var endProfile: BABYLON.Vector3[] = [];
        this.getEllipseContour(
            new BABYLON.Vector2(MeshFactory.convertToLocalCoordinateSystem(coneInfo.Offset), 0),
            MeshFactory.convertToLocalCoordinateSystem(coneInfo.EndRadius),
            MeshFactory.convertToLocalCoordinateSystem(coneInfo.EndRadius),
            MeshFactory.convertToLocalCoordinateSystem(coneInfo.Height / 4),
            0,
            360,
            true,
            complexityLevel,
            true,
            endProfile
        );

        var profileArray: BABYLON.Vector3[][] = [startProfile, endProfile];

        var cone = BABYLON.Mesh.CreateRibbon("cone", profileArray, false, true, 0, this._scene);
        cone.rotation.x = Math.PI / 2;

        var resultMeshes = [cone];
        
        if (coneInfo.Capped) {
            /////////////////
            // START CAP

            // The PolygonMeshBuilder seems to have problems when generating tiny caps.
            // It looks like caps for radius smaller than 2mm may result in invalid
            // vertices being generated.
            if (coneInfo.StartRadius >= 2.0) {
                var capPoints: BABYLON.Vector2[] = [];

                for (var p = 0; p < startProfile.length; p++) {
                    capPoints.push(new BABYLON.Vector2(startProfile[p].x, startProfile[p].y));
                }

                var capBuilder = new BABYLON.PolygonMeshBuilder("cap", capPoints, this._scene);
                var capMesh = capBuilder.build();

                capMesh.position.y += MeshFactory.convertToLocalCoordinateSystem(coneInfo.Height / 2);
                capMesh.bakeCurrentTransformIntoVertices();

                resultMeshes.push(capMesh);
            }

            /////////////////
            // END CAP
            if (coneInfo.EndRadius >= 2.0) {
                var capPoints: BABYLON.Vector2[] = [];

                for (var p = 0; p < endProfile.length; p++) {
                    capPoints.push(new BABYLON.Vector2(endProfile[p].x, endProfile[p].y));
                }

                var capBuilder = new BABYLON.PolygonMeshBuilder("cap", capPoints, this._scene);
                var capMesh = capBuilder.build();

                capMesh.rotation.x = Math.PI;
                capMesh.position.y -= MeshFactory.convertToLocalCoordinateSystem(coneInfo.Height / 2);
                capMesh.bakeCurrentTransformIntoVertices();

                resultMeshes.push(capMesh);
            }
        }

        MeshFactory.setMeshesTransformation(resultMeshes, coneInfo.Position, coneInfo.Rotation);

        return resultMeshes;
    }

    createPolyhedron(id: string, materialId: string, polyhedronInfo: Data.GraphicData.Polyhedron): BABYLON.Mesh[] {
        polyhedronInfo.validate();

        var startProfile: BABYLON.Vector3[] = [];

        if (polyhedronInfo.Clockwise) {
            for (var i = 0; i < polyhedronInfo.StartProfile.length; i++) {
                startProfile.push(new BABYLON.Vector3(
                    MeshFactory.convertToLocalCoordinateSystem(polyhedronInfo.StartProfile[i].X),
                    MeshFactory.convertToLocalCoordinateSystem(polyhedronInfo.StartProfile[i].Y),
                    MeshFactory.convertToLocalCoordinateSystem(polyhedronInfo.StartProfile[i].Z)
                ));
            }
        }
        else {
            for (var i = polyhedronInfo.StartProfile.length - 1; i >= 0; i--) {
                startProfile.push(new BABYLON.Vector3(
                    MeshFactory.convertToLocalCoordinateSystem(polyhedronInfo.StartProfile[i].X),
                    MeshFactory.convertToLocalCoordinateSystem(polyhedronInfo.StartProfile[i].Y),
                    MeshFactory.convertToLocalCoordinateSystem(polyhedronInfo.StartProfile[i].Z)
                ));
            }
        }

        // Remove last point if it's the same as the first one.
        if (startProfile[0].equalsWithEpsilon(startProfile[startProfile.length - 1], MeshFactory.DISTANCE_TOLERANCE)) {
            startProfile.pop();
        }

        var endProfile: BABYLON.Vector3[] = [];
        if (polyhedronInfo.Clockwise) {
            for (var i = 0; i < polyhedronInfo.EndProfile.length; i++) {
                endProfile.push(new BABYLON.Vector3(
                    MeshFactory.convertToLocalCoordinateSystem(polyhedronInfo.EndProfile[i].X),
                    MeshFactory.convertToLocalCoordinateSystem(polyhedronInfo.EndProfile[i].Y),
                    MeshFactory.convertToLocalCoordinateSystem(polyhedronInfo.EndProfile[i].Z)
                ));
            }
        }
        else {

            for (var i = polyhedronInfo.EndProfile.length - 1; i >= 0; i--) {
                endProfile.push(new BABYLON.Vector3(
                    MeshFactory.convertToLocalCoordinateSystem(polyhedronInfo.EndProfile[i].X),
                    MeshFactory.convertToLocalCoordinateSystem(polyhedronInfo.EndProfile[i].Y),
                    MeshFactory.convertToLocalCoordinateSystem(polyhedronInfo.EndProfile[i].Z)
                ));
            }
        }

        // Remove last point if it's the same as the first one.
        if (endProfile[0].equalsWithEpsilon(endProfile[endProfile.length - 1], MeshFactory.DISTANCE_TOLERANCE)) {
            endProfile.pop();
        }

        var profileArray: BABYLON.Vector3[][] = [startProfile, endProfile];

        var polyhedron = BABYLON.Mesh.CreateRibbon("polyhedron", profileArray, false, true, 0, this._scene);
        this.calculateMeshNormals(polyhedron);
        polyhedron.convertToFlatShadedMesh();

        var resultMeshes = [polyhedron];

        if (polyhedronInfo.Capped) {

            var profileDir = startProfile[0].subtract(endProfile[0]).normalize();

            /////////////////
            // START CAP
            var capMesh = this.generateProfileCap(startProfile, profileDir);
            if (capMesh) {
                resultMeshes.push(capMesh);
            }

            /////////////////
            // END CAP
            capMesh = this.generateProfileCap(endProfile, profileDir.negate());

            if (capMesh) {
                resultMeshes.push(capMesh);
            }
        }

        MeshFactory.setMeshesTransformation(resultMeshes, polyhedronInfo.Position, polyhedronInfo.Rotation);

        return resultMeshes;
    }

    generateProfileCap(profile: BABYLON.Vector3[], profileDir: BABYLON.Vector3): BABYLON.Mesh {

        var origin = profile[0];

        var v1: BABYLON.Vector3;
        var v2: BABYLON.Vector3;

        for (var i = 1; i < profile.length; i++) {
            if (!profile[i].equals(origin)) {
                for (var x = i + 1; x < profile.length; x++) {
                    if (!profile[x].equals(origin) && !profile[x].equals(profile[i])) {
                        v1 = profile[i].subtract(origin);
                        v2 = profile[x].subtract(origin);

                        x = profile.length;
                        i = profile.length;
                    }
                }
            }
        }


        if (v1 == null || v2 == null) {
            // Can only generate caps if there are 3 or more unique points in the profile!
            return null;
        }

        var dir = BABYLON.Vector3.Cross(v2, v1).normalize();
        var ori = BABYLON.Vector3.Cross(v1, dir).normalize();
        var v1norm = v1.normalize();

        // Check if we need to flip the direction (normal) of the generated cap.
        var angle = BABYLON.Vector3.Dot(dir, profileDir);
        if (angle < 0) {

            dir.scaleInPlace(-1);
            
            var vTemp = ori;
            ori = v1norm;
            v1norm = vTemp;
        }

        var matrix = BABYLON.Matrix.FromValues(
            v1norm.x, v1norm.y, v1norm.z, 0,
            dir.x, dir.y, dir.z, 0,
            ori.x, ori.y, ori.z, 0,
            origin.x, origin.y, origin.z, 1);

        var matrixTo2D = BABYLON.Matrix.Identity();
        matrix.invertToRef(matrixTo2D);

        var profile2D: BABYLON.Vector2[] = [];

        // In order to use PolygonMeshBuilder, we must transform the 3d points onto a 2d plane. We "lock" the y axis, and use the
        // x, z points of the polygon.
        for (var p = 0; p < profile.length; p++) {

            var point2D = BABYLON.Vector3.TransformCoordinates(profile[p], matrixTo2D);
            profile2D.push(new BABYLON.Vector2(point2D.x, point2D.z));
        }

        var faceBuilder = new BABYLON.PolygonMeshBuilder("cap", profile2D, this._scene);
        var faceMesh = faceBuilder.build();

        // Since the points were transformed to 2d, we now rotate them back into world coordinates.
        faceMesh.rotationQuaternion = BABYLON.Quaternion.FromRotationMatrix(matrix);
        faceMesh.position = origin;

        return faceMesh;
    }

    createSphere(id: string, materialId: string, sphereInfo: Data.GraphicData.Sphere): BABYLON.Mesh {
        sphereInfo.validate();

        var lvl = this.getSphereComplexityLevel(sphereInfo.Radius);
        
        var sphere: BABYLON.Mesh = BABYLON.Mesh.CreateSphere(id, lvl, sphereInfo.Radius * 2 / 1000, this._scene);

        sphere.id = id;
        sphere.position.x = sphereInfo.Position.X / 1000;
        sphere.position.y = sphereInfo.Position.Y / 1000;
        sphere.position.z = sphereInfo.Position.Z / 1000;
        
        return sphere;
    }

    createTripodInstances(scanDictionary: { [n: string]: Data.GraphicData.Vector3D }, material: BABYLON.StandardMaterial): BABYLON.InstancedMesh[] {

        var ids = Object.keys(scanDictionary);

        if (this._tripodMeshes[material.id] == null) {

            var leg = BABYLON.Mesh.CreateCylinder("Cylinder", 1, 1, 1, 12, 1, this._scene, true);

            leg.setEnabled(false);
            
            var s1 = BABYLON.Mesh.CreateSphere("", 5, .6, this._scene);
            s1.setEnabled(false);
            s1.position.x = 0;
            s1.position.y = 0;
            s1.position.z = 0;

            var c1o = BABYLON.Mesh.CreateCylinder("Cylinder", 1, 1, 1, 12, 1, this._scene, true);
            c1o.setEnabled(false);

            var c1 = c1o.clone();

            c1.scaling = new BABYLON.Vector3(.11, 2, .11);
            c1.position.x = 0;
            c1.position.y = 0;
            c1.position.z = 0;
            c1.rotate(new BABYLON.Vector3(0.7040633554380183, 0.09268000355371799, 0.7040633554380183), 2.9567377680088947, BABYLON.Space.WORLD);
            c1.translate(new BABYLON.Vector3(0, -.883243, .236664), 1, BABYLON.Space.WORLD);
            c1.translate(new BABYLON.Vector3(0, 1, 0), .18, BABYLON.Space.LOCAL);
            c1.flipFaces(false);
            
            var c2 = leg.clone("");

            c2.scaling = new BABYLON.Vector3(.11, 2, .11);
            c2.position.x = 0;
            c2.position.y = 0;
            c2.position.z = 0;

            c2.rotate(new BABYLON.Vector3(-0.9581980355617428, -0.1261702691134541, 0.2567831533365369), 3.0740136788868395, BABYLON.Space.WORLD);
            c2.translate(new BABYLON.Vector3(.204953, -.883244, -.118332), 1, BABYLON.Space.WORLD);
            c2.translate(new BABYLON.Vector3(0, 1, 0), .18, BABYLON.Space.LOCAL);
            
            var c3 = leg.clone("");

            c3.scaling = new BABYLON.Vector3(.11, 2, .11);
            c3.position.x = 0;
            c3.position.y = 0;
            c3.position.z = 0;
            c3.rotate(new BABYLON.Vector3(-0.2586712182167097, -0.0340604092296788, 0.9653647442233376), 2.8887646918578214, BABYLON.Space.WORLD);
            c3.translate(new BABYLON.Vector3(-.204953, -.883244, -.118332), 1, BABYLON.Space.WORLD);
            c3.translate(new BABYLON.Vector3(0, 1, 0), .18, BABYLON.Space.LOCAL);
            var tripod = BABYLON.Mesh.MergeMeshes([s1,c1, c2, c3], true);

            tripod.setEnabled(false);

            tripod.material = material;

            this._tripodMeshes[material.id] = tripod;
        }
        var meshes = [];

        for (var x = 0; x < ids.length; x++) {

            var scan = this._tripodMeshes[material.id].createInstance(ids[x]);
           
            scan.position.x = scanDictionary[ids[x]].X / 1000;
            scan.position.y = scanDictionary[ids[x]].Y / 1000;
            scan.position.z = scanDictionary[ids[x]].Z / 1000;

            scan.isPickable = true;
            meshes.push(scan);
        }

        return meshes;
    }

    createTripodInstance(id: string, position: Data.GraphicData.Vector3D, material: BABYLON.StandardMaterial): BABYLON.InstancedMesh {

        if (this._tripodMeshes[material.id] == null) {

            var leg = BABYLON.Mesh.CreateCylinder("Cylinder", 1, 1, 1, 12, 1, this._scene, true);

            leg.setEnabled(false);


            var s1 = BABYLON.Mesh.CreateSphere("", 5, .6, this._scene);
            s1.setEnabled(false);
            s1.position.x = 0;
            s1.position.y = 0;
            s1.position.z = 0;

            var c1o = BABYLON.Mesh.CreateCylinder("Cylinder", 1, 1, 1, 12, 1, this._scene, true);
            c1o.setEnabled(false);

            var c1 = c1o.clone();
            c1.scaling = new BABYLON.Vector3(.11, 2, .11);
            c1.position.x = 0;
            c1.position.y = 0;
            c1.position.z = 0;
            c1.rotate(new BABYLON.Vector3(0.7040633554380183, 0.09268000355371799, 0.7040633554380183), 2.9567377680088947, BABYLON.Space.WORLD);
            c1.translate(new BABYLON.Vector3(0, -.883243, .236664), 1, BABYLON.Space.WORLD);
            c1.translate(new BABYLON.Vector3(0, 1, 0), .18, BABYLON.Space.LOCAL);
            c1.flipFaces(false);
            var c2 = leg.clone("");

            c2.scaling = new BABYLON.Vector3(.11, 2, .11);
            c2.position.x = 0;
            c2.position.y = 0;
            c2.position.z = 0;

            c2.rotate(new BABYLON.Vector3(-0.9581980355617428, -0.1261702691134541, 0.2567831533365369), 3.0740136788868395, BABYLON.Space.WORLD);
            c2.translate(new BABYLON.Vector3(.204953, -.883244, -.118332), 1, BABYLON.Space.WORLD);

            c2.translate(new BABYLON.Vector3(0, 1, 0), .18, BABYLON.Space.LOCAL);

            var c3 = leg.clone("");

            c3.scaling = new BABYLON.Vector3(.11, 2, .11);
            c3.position.x = 0;
            c3.position.y = 0;
            c3.position.z = 0;
            c3.rotate(new BABYLON.Vector3(-0.2586712182167097, -0.0340604092296788, 0.9653647442233376), 2.8887646918578214, BABYLON.Space.WORLD);
            c3.translate(new BABYLON.Vector3(-.204953, -.883244, -.118332), 1, BABYLON.Space.WORLD);
            c3.translate(new BABYLON.Vector3(0, 1, 0), .18, BABYLON.Space.LOCAL);
            var tripod = BABYLON.Mesh.MergeMeshes([s1, c1, c2, c3], true);

            tripod.setEnabled(false);

            tripod.material = material;

            this._tripodMeshes[material.id] = tripod;
        }

        var scan = this._tripodMeshes[material.id].createInstance(id);

        scan.position.x = position.X / 1000;
            scan.position.y = position.Y / 1000;
            scan.position.z = position.Z / 1000;

        scan.isPickable = true;
        return scan;
    }

    createExtrusion(id: string, materialId: string, extrusionInfo: Data.GraphicData.Extrusion): BABYLON.Mesh[] {
        extrusionInfo.validate();

        var flatShaded: boolean;
        var profile: BABYLON.Vector3[] = [];

        for (let i = 0; i < extrusionInfo.NumCurves; i++) {
            var curve = extrusionInfo.GetCurve(i);

            if (curve instanceof Data.GraphicData.PolylineCurve) {

                let srcPoints = (curve as Data.GraphicData.PolylineCurve).PointSet;
                let pointsMax = 0.0;

                for (let n = 0; n < srcPoints.length; n++) {
                    profile.push(new BABYLON.Vector3(
                        MeshFactory.convertToLocalCoordinateSystem(srcPoints[n].X),
                        MeshFactory.convertToLocalCoordinateSystem(srcPoints[n].Y),
                        0.0
                    ));

                    pointsMax = Math.max(pointsMax, Math.abs(srcPoints[n].X));
                }

                // Determine the overall normal of the surface defined by the 2D profile
                // by adding up normals of all of the triangles defined by the surface's
                // center and 2 adjacent surface points. The orientation of such overall normal
                // then determines whether the profile is oriented clockwise or counter-clockwise.
                let midPoint = new BABYLON.Vector3(0.0, 0.0, 0.0);
                let profileNorm = new BABYLON.Vector3(0.0, 0.0, 0.0);

                profile.forEach((pt) => midPoint.addInPlace(pt));
                midPoint.scaleInPlace(1 / profile.length);

                for (let i = 0; i < profile.length; i++) {
                    let v1 = profile[i].subtract(midPoint);
                    let v2 = profile[(i + 1) % profile.length].subtract(midPoint);
                    profileNorm.addInPlace(BABYLON.Vector3.Cross(v1, v2));
                }

                profileNorm.normalize();

                if (BABYLON.Vector3.Dot(new BABYLON.Vector3(0, 0, 1), profileNorm) <= 0) {
                    profile.reverse();
                }

                profileSlices = this.getCylinderComplexityLevel(pointsMax, 1.0);

                flatShaded = extrusionInfo.FlatShaded;
            }
            else {
                var srcCurve = curve as Data.GraphicData.EllipseCurve;

                var clockwise = (srcCurve.Clockwise === 1);
                var profileSlices = this.getCylinderComplexityLevel(Math.max(srcCurve.XRadius, srcCurve.YRadius), 1.0);

                this.getEllipseContour(
                    new BABYLON.Vector2(MeshFactory.convertToLocalCoordinateSystem(srcCurve.XCenter), MeshFactory.convertToLocalCoordinateSystem(srcCurve.YCenter)),
                    MeshFactory.convertToLocalCoordinateSystem(srcCurve.XRadius),
                    MeshFactory.convertToLocalCoordinateSystem(srcCurve.YRadius),
                    0.0,
                    srcCurve.StartAngle,
                    srcCurve.EndAngle,
                    !clockwise,
                    profileSlices,
                    false,
                    profile
                );

                flatShaded = false;
            }
        }

        var path: BABYLON.Vector3[] = [];

        var endPoint = MeshFactory.convertToLocalVector3(extrusionInfo.Path[1]);
        var startPoint = MeshFactory.convertToLocalVector3(extrusionInfo.Path[2]);

        path.push(new BABYLON.Vector3(0, 0, endPoint.length()));
        path.push(new BABYLON.Vector3(0, 0, startPoint.length()));

        var resultMeshes: BABYLON.Mesh[] = [];
    
        var extrusion = BABYLON.Mesh.ExtrudeShape("Extrusion", profile, path, 1, 0, BABYLON.Mesh.NO_CAP, this._scene); 
        extrusion.id = id;

        if (flatShaded) {
            this.calculateMeshNormals(extrusion);
            extrusion.convertToFlatShadedMesh();
        }

        var dir = endPoint.subtract(startPoint).normalize();
        var up = new BABYLON.Vector3(extrusionInfo.UpVector.X, extrusionInfo.UpVector.Y, extrusionInfo.UpVector.Z);
        var bodyDirection = endPoint.subtract(startPoint).normalize();
        var xAxis = BABYLON.Vector3.Cross(up, dir).normalize();
        var yAxis = BABYLON.Vector3.Cross(dir, xAxis).normalize();
        var xBodyAxis = BABYLON.Vector3.Cross(up, bodyDirection).normalize();
        var yBodyAxis = BABYLON.Vector3.Cross(bodyDirection, xBodyAxis).normalize();

        var upVectorRotationMatrix = BABYLON.Matrix.FromValues(
            xBodyAxis.x, xBodyAxis.y, xBodyAxis.z, 0,
            yBodyAxis.x, yBodyAxis.y, yBodyAxis.z, 0,
            bodyDirection.x, bodyDirection.y, bodyDirection.z, 0,
            0, 0, 0, 1);

        var capRotationMatrix = BABYLON.Matrix.FromValues(
            xAxis.x, xAxis.y, xAxis.z, 0,
            yAxis.x, yAxis.y, yAxis.z, 0,
            dir.x, dir.y, dir.z, 0,
            0, 0, 0, 1);

        extrusion.rotationQuaternion = BABYLON.Quaternion.FromRotationMatrix(upVectorRotationMatrix);

        // double-sided?
        if (extrusionInfo.Path.length >= 3) {
            var p3 = MeshFactory.convertToLocalVector3(extrusionInfo.Path[3]);
            if (p3.equalsWithEpsilon(endPoint, MeshFactory.Epsilon)) {
                this.makeMeshDoubleSided(extrusion);
            }
        }

        resultMeshes.push(extrusion);

        // Deliberately not doing this call on the caps because it is easier to deal with the local translation needed for the end cap below.
        MeshFactory.setMeshesTransformation(resultMeshes, extrusionInfo.Position, extrusionInfo.Rotation);

        if (extrusionInfo.Capped) {

            // Caps are made "double faced". There are two polygons made for the start cap, and two for the end cap.
            // The strategy for rotating the caps is to first rotate the cap into the proper "local" space. Polygons are made with a 90 degree rotation on the X, Y, and Z axis.
            // Therefore, local rotations are done to put the caps into the right local space. From there we can rotate the same as the above extrusion, using the up vector and then 
            // the rotation.

            var capPoints: BABYLON.Vector2[] = [];

            for (var p = 0; p < profile.length; p++) {
                if (p > 0 && profile[0].equalsWithEpsilon(profile[p], MeshFactory.Epsilon)) {
                    // Some of the profiles have holes in them. This is however not (easily) supported
                    // by the PolygonMeshBuilder. Therefore, we keep only a part of the profile, which
                    // ends with the profile's starting point.
                    break;
                }

                capPoints.push(new BABYLON.Vector2(profile[p].x, profile[p].y));
            }

            try {

                /////////////////
                // START CAP

                var capBuilder = new BABYLON.PolygonMeshBuilder("cap", capPoints, this._scene);
                var capMesh = capBuilder.build();

                capMesh.rotationQuaternion = BABYLON.Quaternion.FromRotationMatrix(capRotationMatrix);

                capMesh.rotate(BABYLON.Axis.Y, Math.PI / 2, BABYLON.Space.LOCAL);
                capMesh.rotate(BABYLON.Axis.X, -Math.PI / 2, BABYLON.Space.LOCAL);
                capMesh.rotate(BABYLON.Axis.Z, Math.PI / 2, BABYLON.Space.LOCAL);

                capMesh.rotate(new BABYLON.Vector3(extrusionInfo.Rotation.X, extrusionInfo.Rotation.Y, extrusionInfo.Rotation.Z), extrusionInfo.Rotation.W, BABYLON.Space.WORLD);

                capMesh.position.x += MeshFactory.convertToLocalCoordinateSystem(extrusionInfo.Position.X);
                capMesh.position.y += MeshFactory.convertToLocalCoordinateSystem(extrusionInfo.Position.Y);
                capMesh.position.z += MeshFactory.convertToLocalCoordinateSystem(extrusionInfo.Position.Z);

                capMesh.flipFaces(true);

                resultMeshes.push(capMesh);

                /////////////////
                // END CAP
                
                var capMesh = capBuilder.build();

                capMesh.rotationQuaternion = BABYLON.Quaternion.FromRotationMatrix(capRotationMatrix);

                capMesh.rotate(BABYLON.Axis.Y, Math.PI / 2, BABYLON.Space.LOCAL);
                capMesh.rotate(BABYLON.Axis.X, -Math.PI / 2, BABYLON.Space.LOCAL);
                capMesh.rotate(BABYLON.Axis.Z, Math.PI / 2, BABYLON.Space.LOCAL);

                capMesh.rotate(new BABYLON.Vector3(extrusionInfo.Rotation.X, extrusionInfo.Rotation.Y, extrusionInfo.Rotation.Z), extrusionInfo.Rotation.W, BABYLON.Space.WORLD);

                capMesh.position.x += MeshFactory.convertToLocalCoordinateSystem(extrusionInfo.Position.X);
                capMesh.position.y += MeshFactory.convertToLocalCoordinateSystem(extrusionInfo.Position.Y);
                capMesh.position.z += MeshFactory.convertToLocalCoordinateSystem(extrusionInfo.Position.Z);

                // Translate the cap along the local Y axis the length of the path. This makes it the end cap.
                capMesh.locallyTranslate(new BABYLON.Vector3(0, endPoint.length() - startPoint.length(), 0));

                resultMeshes.push(capMesh);
            }
            catch (error) {
                console.log("Error creating caps for extrusion.", {
                    graphicalObjectId: id,
                    error: error instanceof Error ? error.message : String(error)
                });
            }
        }

        return resultMeshes;
    }

    RemoveDuplicateConsecutivePoints(pointSet: BABYLON.Vector2[]): BABYLON.Vector2[] {

        
        var retVal = new Array();
        if (pointSet.length > 0) {
            retVal.push(pointSet[0]);
            var lastAdded = pointSet[0];

            for (var i = 1; i < pointSet.length; i++) {
                if (!lastAdded.equals(pointSet[i])) {
                    retVal.push(pointSet[i]);
                    lastAdded = pointSet[i];
                }
            }
        }

        return retVal;
    }

    VerifyPointSetNonIntersecting(pointSet: BABYLON.Vector2[]): boolean {
        for (var p = 0; p < pointSet.length - 1; p++) {
            var a1 = pointSet[p];
            var a2 = pointSet[p + 1];

            for (var i = p + 1; i < pointSet.length - 1; i++) {
                var b1 = pointSet[i];
                var b2 = pointSet[i + 1];


                if (this.LineSegmentsIntersect(a1, a2, b1, b2, (p + 1 == i) || ((i + 2 == pointSet.length) && p == 0) ? true : false)) {
                    return false;
                }
            }
        }

        return true;
    }

    LineSegmentsIntersect(ps1: BABYLON.Vector2, pe1: BABYLON.Vector2, ps2: BABYLON.Vector2, pe2: BABYLON.Vector2, consecutiveLines: boolean): boolean {
        // Get A,B,C of first line - points : ps1 to pe1
        var A1 = pe1.y - ps1.y;
        var B1 = ps1.x - pe1.x;
        var C1 = A1 * ps1.x + B1 * ps1.y;

        // Get A,B,C of second line - points : ps2 to pe2
        var A2 = pe2.y - ps2.y;
        var B2 = ps2.x - pe2.x;
        var C2 = A2 * ps2.x + B2 * ps2.y;

        // Get delta and check if the lines are parallel
        var delta = A1 * B2 - A2 * B1;
        if (delta == 0) {

            // Lines are parallel, so we check if the line segments intersect with each other.
            if (this.PointTouchesLineSegment(ps1, pe1, ps2, false) || this.PointTouchesLineSegment(ps1, pe1, pe2, false)) {
                return true;
            }

            return false;
        }
   
        var xPoint = (B2 * C1 - B1 * C2) / delta;
        var yPoint = (A1 * C2 - A2 * C1) / delta;

        var intersectPoint = new BABYLON.Vector2(xPoint, yPoint);

        if (!consecutiveLines) {
            // Lines are not consecutive, and non parallel. Therefore, we must make sure the intersection point does not hit either of the two line segments.
            if (this.PointTouchesLineSegment(ps1, pe1, intersectPoint, false) && this.PointTouchesLineSegment(ps2, pe2, intersectPoint, false)) {
                return true;
            }
        }

        return false;
    }

    ProfileClockwise(profile: BABYLON.Vector2[]): boolean {

        var sum = 0;

        for (var x = 0; x < profile.length - 1; x++) {

            sum += (profile[x + 1].x - profile[x].x) * (profile[x + 1].y + profile[x].y);
        }

        if (sum == 0)
            console.log("sum 0");

        return sum > 0;
    }
    PointTouchesLineSegment(p1: BABYLON.Vector2, p2: BABYLON.Vector2, p3: BABYLON.Vector2, endsAllowedToTouch: boolean): boolean {

        var colinear = false;

        if ((p2.x - p1.x) != 0) {
            var m = (p2.y - p1.y) / (p2.x - p1.x);
            var b = p1.y - m*p1.x;

            if (Math.abs((p3.y - b) -  (m * p3.x)) < .0001) {
                colinear = true;
            }
        }
        else {
            if (p1.x == p3.x)
                colinear = true;
        }

        if (colinear) {
            // Now check if they actually overlap

            if ((p3.x >= Math.min(p1.x, p2.x) && p3.x <= Math.max(p1.x, p2.x)) &&
                (p3.y >= Math.min(p1.y, p2.y) && p3.y <= Math.max(p1.y, p2.y))) {

                if (endsAllowedToTouch) {
                    if ((Math.abs(p1.x - p3.x) < .0001 && Math.abs(p1.y - p3.y) < .0001) ||
                        (Math.abs(p2.x - p3.x) < .0001 && Math.abs(p2.y - p3.y) < .0001)) {
                        return false;
                    }
                }
                
                return true;
            }
        }

        return false;
    }

    createPyramid(id: string, materialId: string, pyramidInfo: Data.GraphicData.Pyramid): BABYLON.Mesh {
        pyramidInfo.validate();

        var pyramid: BABYLON.Mesh = new BABYLON.Mesh(id, this._scene);

        var offset = pyramidInfo.Offset;
        var botW = (pyramidInfo.StartSize.X) / 2;
        var botH = (pyramidInfo.StartSize.Y) / 2;

        var topW = (pyramidInfo.EndSize.X) / 2;
        var topH = (pyramidInfo.EndSize.Y) / 2;

        var indices: any[] = [];
        var positions: any[] = [];
        var normals: any[] = [];
        
        // Generate points for Shape
        //     BOTTOM           TOP
        //	1--------2       5--------6
        //	|        |       |        |
        //  |        |       |        |
        //	0--------3       4--------7

        var p0 = new BABYLON.Vector3(-botH, 0.0, -botW);
        var p1 = new BABYLON.Vector3(-botH, 0.0, botW);
        var p2 = new BABYLON.Vector3(botH, 0.0, botW);
        var p3 = new BABYLON.Vector3(botH, 0.0, -botW);

        var p4 = new BABYLON.Vector3(-topH + offset.X, pyramidInfo.Height, -topW + offset.Y);
        var p5 = new BABYLON.Vector3(-topH + offset.X, pyramidInfo.Height, topW + offset.Y);
        var p6 = new BABYLON.Vector3(topH + offset.X, pyramidInfo.Height, topW + offset.Y);
        var p7 = new BABYLON.Vector3(topH + offset.X, pyramidInfo.Height, -topW + offset.Y);

        p0.scaleInPlace(1 / 1000);
        p1.scaleInPlace(1 / 1000);
        p2.scaleInPlace(1 / 1000);
        p3.scaleInPlace(1 / 1000);
        p4.scaleInPlace(1 / 1000);
        p5.scaleInPlace(1 / 1000);
        p6.scaleInPlace(1 / 1000);
        p7.scaleInPlace(1 / 1000);

        var v1v2: BABYLON.Vector3 = null;
        var v2v3: BABYLON.Vector3 = null;
        var normal: BABYLON.Vector3 = null;

        //Front
        v1v2 = p0.subtract(p3);
        v2v3 = p7.subtract(p3);
        normal = BABYLON.Vector3.Cross(v1v2, v2v3);

        this.tesselateRectangle(p3, p7, p4, p0, normal, indices, positions, normals);

        //Back
        v1v2 = p2.subtract(p1);
        v2v3 = p5.subtract(p1);
        normal = BABYLON.Vector3.Cross(v1v2, v2v3);

        this.tesselateRectangle(p1, p5, p6, p2, normal, indices, positions, normals);

        //Left
        v1v2 = p1.subtract(p0);
        v2v3 = p4.subtract(p0);
        normal = BABYLON.Vector3.Cross(v1v2, v2v3);

        this.tesselateRectangle(p0, p4, p5, p1, normal, indices, positions, normals);

        //Right
        v1v2 = p3.subtract(p2);
        v2v3 = p6.subtract(p2);
        normal = BABYLON.Vector3.Cross(v1v2, v2v3);

        this.tesselateRectangle(p2, p6, p7, p3, normal, indices, positions, normals);

        //Top
        v1v2 = p5.subtract(p7);
        v2v3 = p6.subtract(p7);
        normal = BABYLON.Vector3.Cross(v1v2, v2v3);

        this.tesselateRectangle(p7, p6, p5, p4, normal, indices, positions, normals);

        //Bottom
        v1v2 = p3.subtract(p0);
        v2v3 = p1.subtract(p0);
        normal = BABYLON.Vector3.Cross(v1v2, v2v3);

        this.tesselateRectangle(p0, p1, p2, p3, normal, indices, positions, normals);

        pyramid.setVerticesData(BABYLON.VertexBuffer.PositionKind, positions, true);
        pyramid.setVerticesData(BABYLON.VertexBuffer.NormalKind, normals, true);
        pyramid.setIndices(indices);

        pyramid.rotate(new BABYLON.Vector3(pyramidInfo.Rotation.X, pyramidInfo.Rotation.Y, pyramidInfo.Rotation.Z), pyramidInfo.Rotation.W, BABYLON.Space.WORLD);

        pyramid.id = id;
        pyramid.position.x = pyramidInfo.Position.X / 1000;
        pyramid.position.y = pyramidInfo.Position.Y / 1000;
        pyramid.position.z = pyramidInfo.Position.Z / 1000;

        
        return pyramid;
    }

    private tesselateRectangle(bottomLeft: BABYLON.Vector3, topLeft: BABYLON.Vector3, topRight: BABYLON.Vector3, bottomRight: BABYLON.Vector3, normal: BABYLON.Vector3, indicies: any[], points: any[], normals: any[]) {
        var pointsStart = points.length / 3;

        indicies.push(0 + pointsStart);
        indicies.push(1 + pointsStart);
        indicies.push(2 + pointsStart);

        indicies.push(0 + pointsStart);
        indicies.push(2 + pointsStart);
        indicies.push(3 + pointsStart);

        points.push(bottomLeft.x, bottomLeft.y, bottomLeft.z);
        normals.push(normal.x, normal.y, normal.z);
        points.push(topLeft.x, topLeft.y, topLeft.z);
        normals.push(normal.x, normal.y, normal.z);
        points.push(topRight.x, topRight.y, topRight.z);
        normals.push(normal.x, normal.y, normal.z);
        points.push(bottomRight.x, bottomRight.y, bottomRight.z);
        normals.push(normal.x, normal.y, normal.z);
    }

    private getSphereComplexityLevel(radius: number): number {
        var numSegments = 9;
        // Get the number of segments based on the size...
        if (radius < 250.0) {
            numSegments = 3;
        }
        else if (radius < 500.0) {
            numSegments = 5;
        }
        else if (radius < 1000.0) {
            numSegments = 7;
        }

        return numSegments;
    }

    private getCylinderComplexityLevel(radius: number, complexity: number): number {
        var numSegments = 35;
        // Get the number of segments based on the size...
        if (radius < 20.0) {
            numSegments = 4;
        }
        else if (radius < 45.0) {
            numSegments = 7;
        }
        else if (radius < 65.0) {
            numSegments = 9;
        }
        else if (radius < 95.0) {
            numSegments = 12;
        }
        else if (radius < 185.0) {
            numSegments = 15;
        }
        else if (radius < 320.0) {
            numSegments = 19;
        }
        else if (radius < 650.0) {
            numSegments = 25;
        }

        return numSegments;
    }

    // This will create a singleton cylinder mesh for a complexity level if one has not been created yet, then will
    // will return the singleton cylinder mesh.
    private getComplexityDefinedCylinderMesh(complexityLevel: number): BABYLON.Mesh {
        if (this._cylinderMasterMeshes[complexityLevel] != null) {
            return this._cylinderMasterMeshes[complexityLevel];
        }
        else {

            // complexity cannot be found for this mesh, so create a new cylinder for this complexity level.
            var cylinderMesh = BABYLON.Mesh.CreateCylinder("Cylinder", 1, 1, 1, complexityLevel, 1, this._scene, true);
            cylinderMesh.setEnabled(false);

            this._cylinderMasterMeshKeys.push(complexityLevel);
            this._cylinderMasterMeshes[complexityLevel] = cylinderMesh;

            return cylinderMesh;
        }
    }

    private getComplexityDefinedCylinderGeometry(complexityLevel: number): WorkerGeometry.GeometryData {
        if (this._cylinderMasterGeometries[complexityLevel] != null) {
            return this._cylinderMasterGeometries[complexityLevel];
        }

        this._cylinderMasterGeometries[complexityLevel] = WorkerGeometry.Utils.fromMesh(this.getComplexityDefinedCylinderMesh(complexityLevel));

        return this._cylinderMasterGeometries[complexityLevel];
    }

    private getComplexityDefinedSphereGeometry(level: number): WorkerGeometry.GeometryData {
        if (this._sphereMasterGeometries[level] != null) {
            return this._sphereMasterGeometries[level];
        }

        var sphere = BABYLON.Mesh.CreateSphere("Sphere", level, 1, this._scene);
        sphere.setEnabled(false);
        this._sphereMasterGeometries[level] = WorkerGeometry.Utils.fromMesh(sphere, true);

        return this._sphereMasterGeometries[level];
    }
    private getComplexityDefinedCylinderVertices(complexityLevel: number, mesh: BABYLON.Mesh): void {
        if (this._cylinderMasterMeshes[complexityLevel] != null) {
            var positions = this._cylinderMasterMeshes[complexityLevel].getVerticesData(BABYLON.VertexBuffer.PositionKind);
            var copiedPositions: any[] = [];
            for (var i = 0; i < positions.length; i++) {
                copiedPositions.push(positions[i]);
            }

            var normals = this._cylinderMasterMeshes[complexityLevel].getVerticesData(BABYLON.VertexBuffer.NormalKind);
            var copiedNormals: any[] = [];
            for (var i = 0; i < normals.length; i++) {
                copiedNormals.push(normals[i]);
            }

            var indices = this._cylinderMasterMeshes[complexityLevel].getIndices();
            var copiedIndeces: any[] = [];
            for (var i = 0; i < indices.length; i++) {
                copiedIndeces.push(indices[i]);
            }

            mesh.setVerticesData(BABYLON.VertexBuffer.PositionKind, copiedPositions, true);
            mesh.setVerticesData(BABYLON.VertexBuffer.NormalKind, copiedNormals, true);
            mesh.setIndices(copiedIndeces);
        }
        else {
            // complexity cannot be found for this mesh, so create a new cylinder for this complexity level.
            var cylinderMesh = BABYLON.Mesh.CreateCylinder("Cylinder", 1, 1, 1, complexityLevel, 1, this._scene);
            cylinderMesh.subMeshes = [];
            cylinderMesh.setEnabled(false);

            this._cylinderMasterMeshKeys.push(complexityLevel);
            this._cylinderMasterMeshes[complexityLevel] = cylinderMesh;

            var positions = cylinderMesh.getVerticesData(BABYLON.VertexBuffer.PositionKind);
            var copiedPositions: any[] = [];
            for (var i = 0; i < positions.length; i++) {
                copiedPositions.push(positions[i]);
            }

            var normals = cylinderMesh.getVerticesData(BABYLON.VertexBuffer.NormalKind);
            var copiedNormals: any[] = [];
            for (var i = 0; i < normals.length; i++) {
                copiedNormals.push(normals[i]);
            }

            var indices = cylinderMesh.getIndices();
            var copiedIndeces: any[] = [];
            for (var i = 0; i < indices.length; i++) {
                copiedIndeces.push(indices[i]);
            }

            mesh.setVerticesData(BABYLON.VertexBuffer.PositionKind, copiedPositions, true);
            mesh.setVerticesData(BABYLON.VertexBuffer.NormalKind, copiedNormals, true);
            mesh.setIndices(copiedIndeces);
        }
    }

    createShell(id: string, materialId: string, shellInfo: Data.GraphicData.Shell): BABYLON.Mesh[] {
        shellInfo.validate();
        return this.createPolygonCollectionCommon(id, materialId, shellInfo.Vertices, shellInfo.Indices, shellInfo.Colors, shellInfo.Position);
    }

    createPolygonCollection(id: string, materialId: string, polygonCollectionInfo: Data.GraphicData.PolygonCollection): BABYLON.Mesh[] {
        polygonCollectionInfo.validate();
        return this.createPolygonCollectionCommon(id, materialId, polygonCollectionInfo.Vertices, polygonCollectionInfo.Indices);
    }

    private unpackColors(packedColors: number[]): Float32Array {
        const unpackedColors: Float32Array = new Float32Array(packedColors.length * 4);
        let index = 0;

        for (const packedColor of packedColors) {
            unpackedColors[index++] = ((packedColor >> 24) & 0xFF) / 255.0;
            unpackedColors[index++] = ((packedColor >> 16) & 0xFF) / 255.0;
            unpackedColors[index++] = ((packedColor >> 8) & 0xFF) / 255.0;
            unpackedColors[index++] = (packedColor & 0xFF) / 255.0;
        }

        return unpackedColors;
    }

    private calculateMeshNormals(mesh : BABYLON.Mesh)
    {
        if(mesh.getVerticesData(BABYLON.VertexBuffer.NormalKind) === null)
        {
            const pos = mesh.getVerticesData(BABYLON.VertexBuffer.PositionKind)!;  // Float32Array
            const ind   = mesh.getIndices()!;                                        // Uint32Array | number[]

            const normals: number[] = [];   

            BABYLON.VertexData.ComputeNormals(pos, ind, normals);

            mesh.setVerticesData(BABYLON.VertexBuffer.NormalKind, normals, /*updatable?*/ false);
        }
    }
    private createPolygonCollectionCommon(id: string, materialId: string, colVertices: Data.GraphicData.Vector3D[], colIndices: number[],
        colColors?: number[], position?: Data.GraphicData.Vector3D): BABYLON.Mesh[] {
        var meshes: BABYLON.Mesh[] = [];

        var indices = colIndices;
        var verts = colVertices;

        var isTriangleMesh = false;

        // First detect if the polygon collection is a triangle-only mesh, as triangle
        // meshes have much better creation performance compared to when polygon tesselation
        // is needed.
        if ((indices.length % 5) == 0) {

            // Triangle meshes have 3 actual polygon indices and 2 "control" indices.
            // Also, we need to create a double-sided mesh. Therefore we need to double 
            // the number of allocated indices here.
            var triIndicesCount = (indices.length * 3 / 5) * 2;
            var meshIndices = new Int32Array(triIndicesCount);

            var lastIndex = triIndicesCount - 1;
            var curIndex = 0;
            var numIndex = 0;

            for (var i = 0; i < indices.length; i++) {
                if (numIndex == 3) {
                    if (indices[i] == indices[i - 3]) {
                        numIndex++;
                    }
                    else {
                        isTriangleMesh = false;
                        break;
                    }
                }
                else if (numIndex == 4) {
                    if (indices[i] == 0xffffffff || indices[i] == -1) {
                        numIndex = 0;
                        if (!isTriangleMesh)
                            isTriangleMesh = true;
                    }
                    else {
                        isTriangleMesh = false;
                        break;
                    }
                }
                else {
                    // Indices for the opposite side of the triangle get added in reversed order.
                    meshIndices[curIndex] = meshIndices[lastIndex - curIndex] = indices[i];
                    curIndex++
                    numIndex++;
                }
            }
        }

        if (isTriangleMesh) {
            var positions = new Float32Array(verts.length * 3);
            var iPos = 0;

            for (var i = 0; i < verts.length; i++) {
                positions[iPos++] = verts[i].X / 1000;
                positions[iPos++] = verts[i].Z / 1000;
                positions[iPos++] = verts[i].Y / 1000;
            }

            var mesh = new BABYLON.Mesh("tri", this._scene);
            mesh.setVerticesData(BABYLON.VertexBuffer.PositionKind, positions);
            mesh.setIndices(meshIndices);

            if (colColors && colColors.length > 0) {
                var colors = new Float32Array(this.unpackColors(colColors));
                mesh.setVerticesData(BABYLON.VertexBuffer.ColorKind, colors);
                mesh.useVertexColors = true;
            }

            this.calculateMeshNormals(mesh);
            mesh.convertToFlatShadedMesh();


            meshes.push(mesh);
        }
        else {

            var polyVerts: BABYLON.Vector3[] = [];

            for (var i = 0; i < indices.length; i++) {
                if (indices[i] == 0xffffffff || indices[i] == -1) {

                    if (polyVerts.length > 3) {

                        // When creating a face, some polygon faces may not have a calculated normal that points in the "correct" direction. To deal
                        // with this, we create 2 faces, the "in" face and "out" face. These faces have normals opposite of each other to gaurantee 
                        // that a face is created for the "outside" of the polygon.

                        var faceNormal = this.computeNormal(polyVerts);
                        faceNormal.normalize();

                        var normalIn = faceNormal.scale(-1);

                        var matrixIn = BABYLON.Matrix.Identity();
                        var matrixOut = BABYLON.Matrix.Identity();

                        var T = polyVerts[0];

                        // Find a second vertex, which isn't too close (isn't almost equal)
                        // to the first point. If the two points are too close, the calculated
                        // orientation will most likely be wrong.
                        for (var iPV = 1; iPV < (polyVerts.length - 1); iPV++) {
                            var P = polyVerts[iPV];
                            if (!P.equalsWithEpsilon(T, 1e-4)) {
                                break;
                            }
                        }

                        var v1v2 = P.subtract(T).normalize();
                        var orientation = BABYLON.Vector3.Cross(v1v2, normalIn);

                        matrixIn.setRowFromFloats(0, v1v2.x, v1v2.y, v1v2.z, matrixIn.m[3]);
                        matrixIn.setRowFromFloats(1, normalIn.x, normalIn.y, normalIn.z, matrixIn.m[7]);
                        matrixIn.setRowFromFloats(2, orientation.x, orientation.y, orientation.z, matrixIn.m[11]);
                        matrixIn.setRowFromFloats(3, polyVerts[0].x, polyVerts[0].y, polyVerts[0].z, 1.0);
                        // Flip the normal, and the axis for the surface going the other direction.
                        normalIn = normalIn.scale(-1);
                        var temp = orientation;
                        orientation = v1v2;
                        v1v2 = temp;

                        matrixOut.setRowFromFloats(0, v1v2.x, v1v2.y, v1v2.z, matrixOut.m[3]);
                        matrixOut.setRowFromFloats(1, normalIn.x, normalIn.y, normalIn.z, matrixOut.m[7]);
                        matrixOut.setRowFromFloats(2, orientation.x, orientation.y, orientation.z, matrixOut.m[11]);
                        matrixOut.setRowFromFloats(3, polyVerts[0].x, polyVerts[0].y, polyVerts[0].z, 1.0);
                        var matrixInTo2D = BABYLON.Matrix.Identity();
                        matrixIn.invertToRef(matrixInTo2D);

                        var matrixOutTo2D = BABYLON.Matrix.Identity();
                        matrixOut.invertToRef(matrixOutTo2D);

                        var pointsIn: BABYLON.Vector2[] = [];
                        var pointsOut: BABYLON.Vector2[] = [];

                        // In order to use PolygonMeshBuilder, we must tranform the 3d points onto a 2d plane. We "lock" the y axis, and use the
                        // x, z points of the polygon.
                        for (var p = 0; p < polyVerts.length; p++) {

                            var transformedPointIn = BABYLON.Vector3.TransformCoordinates(polyVerts[p], matrixInTo2D);
                            var transformedPointOut = BABYLON.Vector3.TransformCoordinates(polyVerts[p], matrixOutTo2D);

                            pointsIn.push(new BABYLON.Vector2(transformedPointIn.x, transformedPointIn.z));
                            pointsOut.push(new BABYLON.Vector2(transformedPointOut.x, transformedPointOut.z));
                        }

                        pointsOut = this.RemoveDuplicateConsecutivePoints(pointsOut);
                        pointsIn = this.RemoveDuplicateConsecutivePoints(pointsIn);

                        if (this.VerifyPointSetNonIntersecting(pointsIn)) {

                            try {

                                var faceBuilderIn = new PolygonMeshBuilder("cap", pointsIn, this._scene);

                                var faceMeshIn = faceBuilderIn.build();

                                // Since the points were transformed to 2d, we now rotate them back into world coordinates.
                                faceMeshIn.rotationQuaternion = BABYLON.Quaternion.FromRotationMatrix(matrixIn)
                                faceMeshIn.position = polyVerts[0];

                                this.makeMeshDoubleSided(faceMeshIn);

                                meshes.push(faceMeshIn);
                            }
                            catch (error) {
                                console.log("Error creating caps for polygon collection.", {
                                    graphicalObjectId: id,
                                    error: error instanceof Error ? error.message : String(error)
                                });
                            }
                        }
                    }

                    polyVerts = [];
                }
                else {
                    polyVerts.push(new BABYLON.Vector3(verts[indices[i]].X / 1000, verts[indices[i]].Z / 1000, verts[indices[i]].Y / 1000));
                }
            }
        }

        if (position) {
            MeshFactory.setMeshesTransformation(meshes, position);
        }

        return meshes;
    }

    private makeMeshDoubleSided(mesh: BABYLON.Mesh) {

        var maxIndex = 0;

        var positions = <number[]>Array.prototype.slice.call(mesh.getVertexBuffer(BABYLON.VertexBuffer.PositionKind).getData());
        var indices = mesh.getIndices();
        var normals = <number[]>Array.prototype.slice.call(mesh.getVertexBuffer(BABYLON.VertexBuffer.NormalKind).getData());

        // double-sided
        var lp = (positions.length / 3);
        var li = indices.length;
        var indices2: any[] = [];

        for (var i = 0; i < li; i += 3) {

            maxIndex = maxIndex < indices[i] ? indices[i] : maxIndex;
            maxIndex = maxIndex < indices[i + 1] ? indices[i+ 1] : maxIndex;
            maxIndex = maxIndex < indices[i + 2] ? indices[i + 2] : maxIndex;
            indices2.push(indices[i], indices[i + 1], indices[i + 2]);
        }

        for (var i = 0; i < li; i += 3) {

            indices2.push(lp + indices[i + 2], lp + indices[i + 1], lp + indices[i]);
        }

        var pc = positions.length;
        for (var i = 0; i < pc; i++) {
            positions.push(positions[i]);
        }

        var ln = normals.length;
        for (var i = 0; i < ln; i++) {

            var flippedNormal = normals[i] == 0 ? 0 : -1 * normals[i];

            normals.push(flippedNormal);
        };

        // vertexData
        mesh.setVerticesData(BABYLON.VertexBuffer.PositionKind, positions, false);
        mesh.setVerticesData(BABYLON.VertexBuffer.NormalKind, normals, false);
        mesh.setIndices(indices2);
    }
    private computeNormal(points: BABYLON.Vector3[]): BABYLON.Vector3 {

        var faceNormal = new BABYLON.Vector3(0.0, 0.0, 0.0);

        for (var w = 0; w < points.length - 1; w++) {
            var next = (w + 1) % points.length;
            faceNormal.x += (points[w].z - points[next].z) * (points[w].y + points[next].y);
            faceNormal.z += (points[w].y - points[next].y) * (points[w].x + points[next].x);
            faceNormal.y += (points[w].x - points[next].x) * (points[w].z + points[next].z);
        }
                    
        return faceNormal;
    }
}

class IndexedVector2 extends BABYLON.Vector2 {
    constructor(original: BABYLON.Vector2, public index: number) {
        super(original.x, original.y);
    }
}

class PolygonPoints {
    elements = new Array<IndexedVector2>();

    add(originalPoints: Array<BABYLON.Vector2>): Array<IndexedVector2> {

        var result = new Array<IndexedVector2>();
        originalPoints.forEach(point => {
            if (result.length === 0 || !point.equalsWithEpsilon(result[0]), MeshFactory.Epsilon) {
                var newPoint = new IndexedVector2(point, this.elements.length);
                result.push(newPoint);
                this.elements.push(newPoint);
            }
        });

        return result;
    }

    computeBounds(): { min: BABYLON.Vector2; max: BABYLON.Vector2; width: number; height: number } {
        var lmin = new BABYLON.Vector2(this.elements[0].x, this.elements[0].y);
        var lmax = new BABYLON.Vector2(this.elements[0].x, this.elements[0].y);

        this.elements.forEach(point => {

            // x
            if (point.x < lmin.x) {
                lmin.x = point.x;
            }
            else if (point.x > lmax.x) {
                lmax.x = point.x;
            }

            // y
            if (point.y < lmin.y) {
                lmin.y = point.y;
            }
            else if (point.y > lmax.y) {
                lmax.y = point.y;
            }

        });

        return {
            min: lmin,
            max: lmax,
            width: lmax.x - lmin.x,
            height: lmax.y - lmin.y
        };
    }
}

class Polygon {
    static Rectangle(xmin: number, ymin: number, xmax: number, ymax: number): BABYLON.Vector2[] {
        return [
            new BABYLON.Vector2(xmin, ymin),
            new BABYLON.Vector2(xmax, ymin),
            new BABYLON.Vector2(xmax, ymax),
            new BABYLON.Vector2(xmin, ymax)
        ];
    }

    static Circle(radius: number, cx: number = 0, cy: number = 0, numberOfSides: number = 32): BABYLON.Vector2[] {
        var result = new Array<BABYLON.Vector2>();

        var angle = 0;
        var increment = (Math.PI * 2) / numberOfSides;

        for (var i = 0; i < numberOfSides; i++) {
            result.push(new BABYLON.Vector2(
                cx + Math.cos(angle) * radius,
                cy + Math.sin(angle) * radius
            ));
            angle -= increment;
        }

        return result;
    }

    static Parse(input: string): BABYLON.Vector2[] {
        var floats = input.split(/[^-+eE\.\d]+/).map(parseFloat).filter(val => (!isNaN(val)));
        var i: number, result: any = [];
        for (i = 0; i < (floats.length & 0x7FFFFFFE); i += 2) {
            result.push(new BABYLON.Vector2(floats[i], floats[i + 1]));
        }
        return result;
    }

    static StartingAt(x: number, y: number): BABYLON.Path2 {
        return BABYLON.Path2.StartingAt(x, y);
    }
}

class PolygonMeshBuilder {

    private _points = new PolygonPoints();
    private _outlinepoints = new PolygonPoints();
    private _holes: any = [];

    private _name: string = "";
    private _scene: BABYLON.Scene;

    private _epoints: number[] = new Array<number>();
    private _eholes: number[] = new Array<number>();

    private _addToepoint(points: BABYLON.Vector2[]) {
        for (let p of points) {
            this._epoints.push(p.x, p.y);
        }
    }

    constructor(name: string, contours: BABYLON.Path2, scene: BABYLON.Scene)
    constructor(name: string, contours: BABYLON.Vector2[], scene: BABYLON.Scene)
    constructor(name: string, contours: any, scene: BABYLON.Scene) {
        this._name = name;
        this._scene = scene;

        var points: BABYLON.Vector2[];
        if (contours instanceof BABYLON.Path2) {
            points = (<BABYLON.Path2>contours).getPoints();
        } else {
            points = (<BABYLON.Vector2[]>contours);
        }

        this._addToepoint(points);

        this._points.add(points);
        this._outlinepoints.add(points);
    }

    addHole(hole: BABYLON.Vector2[]): PolygonMeshBuilder {
        this._points.add(hole);
        var holepoints = new PolygonPoints();
        holepoints.add(hole);
        this._holes.push(holepoints);

        this._eholes.push(this._epoints.length / 2);
        this._addToepoint(hole);

        return this;
    }

    build(updatable: boolean = false, depth?: number): BABYLON.Mesh {
        var result = new BABYLON.Mesh(this._name, this._scene);

        var normals: any = [];
        var positions: any = [];
        var uvs: any = [];

        var bounds = this._points.computeBounds();
        this._points.elements.forEach((p) => {
            normals.push(0, 1.0, 0);
            positions.push(p.x, 0, p.y);
            uvs.push((p.x - bounds.min.x) / bounds.width, (p.y - bounds.min.y) / bounds.height);
        });

        var indices: any = [];

        const geometryScope = typeof globalThis !== 'undefined' ? (globalThis as any) : null;
        const triangulate = geometryScope?.earcut;

        if (typeof triangulate !== 'function') {
            throw new Error('earcut is not available in the worker geometry scope.');
        }

        let res = triangulate(this._epoints, this._eholes, 2);

        for (let i = 0; i < res.length; i++) {
            indices.push(res[i]);
        }

        if (depth > 0) {
            var positionscount = (positions.length / 3); //get the current pointcount

            this._points.elements.forEach((p) => { //add the elements at the depth
                normals.push(0, -1.0, 0);
                positions.push(p.x, -depth, p.y);
                uvs.push(1 - (p.x - bounds.min.x) / bounds.width, 1 - (p.y - bounds.min.y) / bounds.height);
            });

            let totalCount = indices.length;
            for (let i = 0; i < totalCount; i += 3) {
                let i0 = indices[i + 0];
                let i1 = indices[i + 1];
                let i2 = indices[i + 2];

                indices.push(i2 + positionscount);
                indices.push(i1 + positionscount);
                indices.push(i0 + positionscount);
            }

            //Add the sides
            this.addSide(positions, normals, uvs, indices, bounds, this._outlinepoints, depth, false);

            this._holes.forEach((hole: any) => {
                this.addSide(positions, normals, uvs, indices, bounds, hole, depth, true);
            });
        }

        result.setVerticesData(BABYLON.VertexBuffer.PositionKind, positions, updatable);
        result.setVerticesData(BABYLON.VertexBuffer.NormalKind, normals, updatable);
        result.setVerticesData(BABYLON.VertexBuffer.UVKind, uvs, updatable);
        result.setIndices(indices);

        return result;
    }

    private addSide(positions: any[], normals: any[], uvs: any[], indices: any[], bounds: any, points: PolygonPoints, depth: number, flip: boolean) {
        var StartIndex: number = positions.length / 3;
        var ulength: number = 0;
        for (var i: number = 0; i < points.elements.length; i++) {
            var p: IndexedVector2 = points.elements[i];
            var p1: IndexedVector2
            if ((i + 1) > points.elements.length - 1) {
                p1 = points.elements[0];
            }
            else {
                p1 = points.elements[i + 1];
            }

            positions.push(p.x, 0, p.y);
            positions.push(p.x, -depth, p.y);
            positions.push(p1.x, 0, p1.y);
            positions.push(p1.x, -depth, p1.y);

            var v1 = new BABYLON.Vector3(p.x, 0, p.y);
            var v2 = new BABYLON.Vector3(p1.x, 0, p1.y);
            var v3 = v2.subtract(v1);
            var v4 = new BABYLON.Vector3(0, 1, 0);
            var vn = BABYLON.Vector3.Cross(v3, v4);
            vn = vn.normalize();

            uvs.push(ulength / bounds.width, 0);
            uvs.push(ulength / bounds.width, 1);
            ulength += v3.length();
            uvs.push((ulength / bounds.width), 0);
            uvs.push((ulength / bounds.width), 1);

            if (!flip) {
                normals.push(-vn.x, - vn.y, -vn.z);
                normals.push(-vn.x, -vn.y, -vn.z);
                normals.push(-vn.x, -vn.y, -vn.z);
                normals.push(-vn.x, -vn.y, -vn.z);

                indices.push(StartIndex);
                indices.push(StartIndex + 1);
                indices.push(StartIndex + 2);

                indices.push(StartIndex + 1);
                indices.push(StartIndex + 3);
                indices.push(StartIndex + 2);
            }
            else {
                normals.push(vn.x, vn.y, vn.z);
                normals.push(vn.x, vn.y, vn.z);
                normals.push(vn.x, vn.y, vn.z);
                normals.push(vn.x, vn.y, vn.z);

                indices.push(StartIndex);
                indices.push(StartIndex + 2);
                indices.push(StartIndex + 1);

                indices.push(StartIndex + 1);
                indices.push(StartIndex + 2);
                indices.push(StartIndex + 3);
            }
            StartIndex += 4;
        };
    }
}
export default MeshFactory;